import React, {
  useCallback, useEffect, useState, useRef, useMemo, Fragment, RefObject, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import Card from '../../components/card/card.component';
import Table from '../../components/table/table.component';
import TableHead from '../../components/table-head/table-head.component';
import TableBody from '../../components/table-body/table-body.component';
import TableRow from '../../components/table-row/table-row.component';
import TableCell from '../../components/table-cell/table-cell.component';
import Chip from '../../components/chip/chip.component';
import Tooltip from '../../components/tooltip/tooltip.component';
import DraggableItem from '../../components/draggable-item/draggable-item.component';
import ContentHeader from '../../components/content-header/content-header.component';
import PageTabs from '../../components/page-tabs/page-tabs.component';
import Loader from '../../components/loader/loader.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import TableResize from '../../components/table-resize/table-resize.component';
import LongClickListItem from '../../components/long-click-list-item/long-click-list-item.component';
import LongClickCard from '../../components/long-click-card/long-click-card.component';
import CheckAccess from '../../components/check-access/check-access.component';
import FilteredCell from '../../components/filtered-cell/filtered-cell.component';
import RequiredMark from '../../components/required-mark/required-mark.component';
import FilteredLink from '../../components/filtered-link/filtered-link.component';
import ContextMenu from '../../components/context-menu/context-menu.component';
import ChipLink from '../../components/chip-link/chip-link.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import TableSortLabel from '../../components/table-sort-label/table-sort-label.component';
import TablePagination from '../../components/table-pagination/table-pagination.component';
import NoContentBlock from '../../components/no-content-block/no-content-block.component';
import ActionButton from '../../components/action-button/action-button.component';
import PaddedFlexContainer from '../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../components/flex-container/flex-container.component';
import Checkbox from '../../components/checkbox/checkbox.component';
import TextNoWrapBlock from '../../components/text-no-wrap-block/text-no-wrap-block.component';
import TextBold from '../../components/text-bold/text-bold.component';
import Grid from '../../components/grid/grid.component';
import GridItem from '../../components/grid-item/grid-item.component';
import VisibleCell from '../../components/visible-cell/visible-cell.component';
import List from '../../components/list/list.component';
import ListItemText from '../../components/list-item-text/list-item-text.component';
import ListItemCheckbox from '../../components/list-item-checkbox/list-item-checkbox.component';
import LocationMap from '../../components/location-map/location-map.component';
import LeaveConfirmModal from '../../components/leave-confirm-modal/leave-confirm-modal.component';
import Image from '../../components/image/image.component';
import LetterAvatar from '../../components/letter-avatar/letter-avatar.component';
import Form from '../../components/form/form.component';
import FormContainer from '../../components/form-container/form-container.component';
import FormError from '../../components/form-error/form-error.component';
import FilterPanel from '../../components/filter-panel/filter-panel.component';
import TableCellShort from '../../components/table-cell-short/table-cell-short.component';
import GridItemCardViewBlock from '../../components/grid-item-card-view-block/grid-item-card-view-block.component';
import GridItemCardViewLine from '../../components/grid-item-card-view-line/grid-item-card-view-line.component';
import ExpansionGroupWidget from '../../components/expansion-group-widget/expansion-group-widget.component';
import LastUpdateBlock from '../app/common/last-update-block/last-update-block.component';
import ControlButtonBlock from '../app/common/control-button-block/control-button-block.component';
import ContentListContainerOffset
  from '../app/common/content-list-container-offset/content-list-container-offset.component';
import ExportModal from '../app/controls/export-modal/export-modal.component';
import Controls from '../app/controls/controls.component';
import ContactRow from './contact-row/contact-row.component';
import ContactRowForm from './contact-row-form/contact-row-form.component';
import ContactModal from './contact-modal/contact-modal.component';
import ContactListFilters from './contact-list-filters/contact-list-filters.component';
import EditIcon from '../../icons/edit-icon';
import DeleteItemIcon from '../../icons/delete-item-icon';
import MoreIcon from '../../icons/more-icon';
import EmailIcon from '../../icons/email-icon';
import PhoneIcon from '../../icons/phone-icon';
import PhoneExtraIcon from '../../icons/phone-extra-icon';
import LinkedinIcon from '../../icons/linkedin-icon';
import MapIcon from '../../icons/map-icon';
import ArrowLinkIcon from '../../icons/arrow-link-icon';
import ContactListValidator from './contact-list.validator';
import GoogleMapService from '../../services/google-map-service';
import StructureService from '../../services/structure-service';
import ScreenService from '../../services/screen-service';
import FormatService from '../../services/format-service';
import FormService from '../../services/form-service';
import useEditModeQuery from '../../hooks/use-edit-mode-query';
import useArrowNavigation from '../../hooks/use-arrow-navigation';
import useFormBlock from '../../hooks/use-form-block';
import useFormStructureModified from '../../hooks/use-form-structure-modified';
import useScreenMobile from '../../hooks/use-screen-mobile';
import useScreenSmall from '../../hooks/use-screen-small';
import useScreenMedium from '../../hooks/use-screen-medium';
import useServerFormErrors from '../../hooks/use-server-form-errors';
import {
  getContactListData, saveContactListData, deleteContactList, getContactCompanyByDomainData,
  getContactPersonByEmailData, resetContactCompanyData, resetContactPersonData,
} from './contact-list.action';
import {
  toggleCustomizeMode, toggleEditMode, toggleExportMode, toggleSelectMode, saveTableColumnSettings,
} from '../app/controls/controls.action';
import { getListContactCompanyData, getListTagData } from '../app/search-list/search-list.action';
import { resetFormErrors } from '../app/app.action';
import { FORM_ID_CONTACT_LIST } from '../../constants/system/form-ids';
import { FORMAT_LIST_IMPORT_CONTACT, FORMAT_LIST_EXPORT_CONTACT } from '../../constants/system/format-lists';
import { PAGE_DEFAULT } from '../../constants/system/pages';
import { TABLE_LIMIT_DEFAULT } from '../../constants/system/table-limits';
import { FILTER_VALUE_EMPTY } from '../../constants/system/filter-values';
import { TAB_INDEX_DEFAULT } from '../../constants/system/tab-indexes';
import {
  CONTACT_CREATE, CONTACT_EDIT, CONTACT_LIST_IMPORT, CONTACT_LIST_EXPORT, CONTACT_VIEW, CONTACT_DELETE,
  CONTACT_COMMENT_LIST_VIEW, CONTACT_ATTACHMENT_LIST_VIEW, SEARCH_RESULT_VIEW, CONTACT_LIST_VIEW,
} from '../../constants/entity/permissions';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../constants/system/sort-types';
import { ROUTE_CONTACT_LIST, ROUTE_SEARCH, ROUTE_CONTACT } from '../../constants/system/routes';
import {
  QUERY_PARAM_TAG_ID, QUERY_PARAM_TAB, QUERY_PARAM_PAGE, QUERY_PARAM_LIMIT, QUERY_PARAM_CONTACT_TYPE,
  QUERY_PARAM_LOCATION, QUERY_PARAM_COMPANY_ID, QUERY_PARAM_ASSET_TYPE, QUERY_PARAM_CREATE, QUERY_PARAM_EDIT_MODE,
} from '../../constants/system/query-params';
import {
  FILTER_TYPE_CONTACT_TYPE, FILTER_TYPE_COMPANY_ID, FILTER_TYPE_LOCATION, FILTER_TYPE_ASSET_TYPE, FILTER_TYPE_TAG_ID,
} from '../../constants/system/filter-types';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../constants/entity/contact-types';
import { IMPORT_EXPORT_ENTITY_TYPE_CONTACT_LIST } from '../../constants/system/import-export-entity-types';
import { TABLE_NAME_CONTACT_LIST } from '../../constants/system/table-names';
import { DATA_CONTACT_ID } from '../../constants/system/data-attributes';
import { SETTING_NAME_TABLE_COLUMN_SETTINGS } from '../../constants/entity/setting-names';
import { FILTER_KEY_NAME } from '../../constants/system/filter-keys';
import { MARKER_COLOR_RED } from '../../constants/entity/marker-colors';
import CompanyDefaultImage from '../../static/images/company-default-image.png';
import PersonDefaultImage from '../../static/images/person-default-image.png';

const ContactList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const NAME_CONTACT_GROUP_ALL: string = 'all';
  const ITEM_ID_CONTACT_GROUP_ALL: number = 0;
  const HEADER_NAME_ACTIONS: string = 'actions';
  const ID_PREFIX_CHECKBOX_EXPORT_CARD: string = 'checkbox-export-card';
  const LABEL_CONTACT_TYPE_PERSON: string = 'Person';
  const LABEL_CONTACT_TYPE_COMPANY: string = 'Company';

  const {
    isDataLoaded,
    isEditMode,
    isExportMode,
    isSelectMode,
    isCustomizeMode,
    isCardView,
    isRightDrawerOpen,
    currentUser,
    contactCompanyData,
    contactPersonData,
    contactListData,
    contactTotal,
    filterContactTypeList,
    filterContactCompanyList,
    filterLocationList,
    filterAssetTypeList,
    filterTagList,
    contactCompanyList,
    contactGroupList,
    contactTypeList,
    tagList,
    assetTypeList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    initialValues: initialFormValues,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const isMediumScreen: boolean = useScreenMedium();
  const isSmallScreen: boolean = useScreenSmall();
  const isMobileScreen: boolean = useScreenMobile();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);
  const tableRef: RefObject<HTMLTableElement> | null = useRef(null);
  const headerCellRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const dispatch: ReduxDispatch = useDispatch();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryCreate: boolean | null = locationQueryParams && locationQueryParams[QUERY_PARAM_CREATE]
    ? FormatService.extractBooleanValue(locationQueryParams[QUERY_PARAM_CREATE].toString()) : null;
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const queryPage: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_PAGE]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_PAGE].toString()) : null;
  const queryLimit: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_LIMIT]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_LIMIT].toString()) : null;
  const queryFilterContactType: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_CONTACT_TYPE]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_CONTACT_TYPE].toString()) : null;
  const queryFilterCompanyId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_COMPANY_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_COMPANY_ID].toString()) : null;
  const queryFilterLocation: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_LOCATION]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_LOCATION].toString()) : null;
  const queryFilterAssetType: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_ASSET_TYPE]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_ASSET_TYPE].toString()) : null;
  const queryFilterTagId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAG_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_TAG_ID].toString()) : null;
  const pageTitle: string = 'Relationship Coverage';

  const DEFAULT_SORT: SortParams = useMemo((): SortParams => ({
    field: 'type',
    type: SORT_TYPE_ASC,
  }), []);

  const DEFAULT_FILTERS: Record<string, FilterValue> = useMemo((): Record<string, FilterValue> => ({
    [FILTER_TYPE_CONTACT_TYPE]: queryFilterContactType || null,
    [FILTER_TYPE_COMPANY_ID]: queryFilterCompanyId || null,
    [FILTER_TYPE_LOCATION]: queryFilterLocation || null,
    [FILTER_TYPE_ASSET_TYPE]: queryFilterAssetType || null,
    [FILTER_TYPE_TAG_ID]: queryFilterTagId || null,
  }), [queryFilterContactType, queryFilterCompanyId, queryFilterLocation, queryFilterAssetType]);

  const FILTER_OPTION_MAP: Record<string, FilterOption[]> = useMemo((): Record<string, FilterOption[]> => ({
    [FILTER_TYPE_CONTACT_TYPE]: filterContactTypeList,
    [FILTER_TYPE_COMPANY_ID]: filterContactCompanyList,
    [FILTER_TYPE_LOCATION]: filterLocationList,
    [FILTER_TYPE_ASSET_TYPE]: filterAssetTypeList,
    [FILTER_TYPE_TAG_ID]: filterTagList,
  }), [filterContactTypeList, filterContactCompanyList, filterLocationList, filterAssetTypeList, filterTagList]);

  const FILTER_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_CONTACT_TYPE]: FILTER_KEY_NAME,
    [FILTER_TYPE_ASSET_TYPE]: FILTER_KEY_NAME,
    [FILTER_TYPE_LOCATION]: FILTER_KEY_NAME,
  }), []);

  const FILTER_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_CONTACT_TYPE]: 'Type',
    [FILTER_TYPE_COMPANY_ID]: 'Company',
    [FILTER_TYPE_LOCATION]: 'Location',
    [FILTER_TYPE_ASSET_TYPE]: 'Asset Type',
    [FILTER_TYPE_TAG_ID]: 'Tag',
  }), []);

  const FILTER_LABEL_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({}), []);

  const quickFilters: QuickFilterItem[] = [
    {
      id: 1,
      title: `${FILTER_LABEL_MAP[FILTER_TYPE_CONTACT_TYPE]}: ${LABEL_CONTACT_TYPE_PERSON}`,
      type: FILTER_TYPE_CONTACT_TYPE,
      value: CONTACT_TYPE_PERSON,
    },
    {
      id: 2,
      title: `${FILTER_LABEL_MAP[FILTER_TYPE_CONTACT_TYPE]}: ${LABEL_CONTACT_TYPE_COMPANY}`,
      type: FILTER_TYPE_CONTACT_TYPE,
      value: CONTACT_TYPE_COMPANY,
    },
  ];

  const [isContactModalOpen, setIsContactModalOpen]: [boolean, Function] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen]: [boolean, Function] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);
  const [tabIndex, setTabIndex]: [number | null, Function] = useState(null);
  const [filters, setFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [currentFilters, setCurrentFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [contactPhotoFiles, setContactPhotoFiles]: [Record<string, File>, Function] = useState({});
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isNewContact, setIsNewContact]: [boolean, Function] = useState(false);
  const [selectedContactGroup, setSelectedContactGroup]: [ContactGroup | null, Function] = useState(null);
  const [page, setPage]: [number, Function] = useState(PAGE_DEFAULT);
  const [limit, setLimit]: [number, Function] = useState(TABLE_LIMIT_DEFAULT);
  const [contactActiveIds, setContactActiveIds]: [number[] | null, Function] = useState<StateValueDefault>(null);
  const [selectedRows, setSelectedRows]: [number[], Function] = useState<StateValueDefault>([]);
  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [mainContactAddressList, setMainContactAddressList]: [Record<string, JsonObject> | null, Function]
    = useState<StateValueDefault>(null);
  const [contactAddressesList, setContactAddressesList]: [Record<string, Record<string, JsonObject>> | null, Function]
    = useState(null);
  const [sortParams, setSortParams]: [SortParams, Function] = useState(DEFAULT_SORT);
  const [extendedTableFields, setExtendedTableFields]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);
  const [tableColumnSettings, setTableColumnSettings]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);
  const [withAllRowsSelected, setAllRowsSelected]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState(null);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [inlineSaveContactId, setInlineSaveContactId]: [number | null, Function] = useState(null);
  const [inlineSaveIsConfirm, setInlineSaveIsConfirm]: [boolean | null, Function] = useState(null);
  const [contactPhotoPreviewUrl, setContactPhotoPreviewUrl]: [string | null, Function] = useState(null);
  const [contactAddressNewItems, setContactAddressNewItems]: [Record<string, ContactAddressInputMap>, Function]
    = useState({});
  const [contactAddressDetailsItems, setContactAddressDetailsItems]: [Record<string, number[]>, Function]
    = useState({});

  const fileDropDeps: DepsItem[] = useMemo((): DepsItem[] => [contactPhotoFiles], [contactPhotoFiles]);
  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);
  const tableColumnDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isCustomizeMode, tableColumnSettings,
  ], [isCustomizeMode, tableColumnSettings]);
  const contactCommentListViewPermissions: string[] = useMemo((): string[] => [CONTACT_COMMENT_LIST_VIEW], []);
  const contactAttachmentListViewPermissions: string[] = useMemo((): string[] => [CONTACT_ATTACHMENT_LIST_VIEW], []);
  const contactEditPermissions: string[] = useMemo((): string[] => [CONTACT_EDIT], []);
  const contactViewPermissions: string[] = useMemo((): string[] => [CONTACT_VIEW], []);
  const contactLocationItemList: LocationMapItem[] = useMemo((): LocationMapItem[] => (
    contactListData && contactListData.length
      ? contactListData.map((contactItem: Contact): LocationMapItem => ({
        id: contactItem.id,
        title: contactItem.fullName || null,
        color: MARKER_COLOR_RED,
        amount: null,
        link: `${ROUTE_CONTACT}/${contactItem.id}`,
        imageLink: contactItem.photoThumbnail || contactItem.photo || null,
        latitude: contactItem.address ? GoogleMapService.extractLatitude(contactItem.address) : null,
        longitude: contactItem.address ? GoogleMapService.extractLongitude(contactItem.address) : null,
      })) : []
  ), [contactListData]);

  const contactsSelectedTotal: number = selectedRows ? selectedRows.length : 0;
  const contactsTotal: number = contactListData ? contactListData.length : 0;
  const filterContactType: string | null = (filters && filters[FILTER_TYPE_CONTACT_TYPE]
    && filters[FILTER_TYPE_CONTACT_TYPE].toString()) || null;
  const isFormDataPristine: boolean = isFormPristine && !Object.keys(contactPhotoFiles).length;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode || isExportMode || isSelectMode || isCustomizeMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isExportDisabled: boolean = !selectedRows || !selectedRows.length;
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));
  const withAllFiltersEmpty: boolean = !filters[FILTER_TYPE_COMPANY_ID] && !filters[FILTER_TYPE_LOCATION]
    && !filters[FILTER_TYPE_ASSET_TYPE] && !filters[FILTER_TYPE_CONTACT_TYPE];
  const isUserPlatformAdmin: boolean = currentUser && currentUser.isPlatformAdmin;
  const withFilters: boolean = (!withAllFiltersEmpty || (quickFilters && !!quickFilters.length)) && !isEditMode
    && !isExportMode && !isSelectMode && !isCustomizeMode;
  const withMap: boolean = !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode;
  const withButtons: boolean = isEditMode || isExportMode || isSelectMode;
  const withPagination: boolean = !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode;
  const createButtonTitle: string | null = !isExportMode && !isSelectMode && !isCustomizeMode ? 'New Contact' : '';
  const isCustomizable: boolean = !isSmallScreen;
  const deleteConfirmModalTitle: string = contactActiveIds && contactActiveIds.length > 1
    ? 'Delete Contacts' : 'Delete Contact';
  const deleteConfirmModalText: string = contactActiveIds && contactActiveIds.length > 1
    ? 'Are you sure you want to delete these contacts?'
    : 'Are you sure you want to delete this contact?';
  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onTagSearch: Function = useCallback((query: string): void => {
    dispatch(getListTagData({
      query,
    }));
  }, []);

  const onTagSearchClose: Function = useCallback((): void => {
    dispatch(getListTagData({
      query: null,
    }));
  }, []);

  const onContactAddressAddNewItem: Function = useCallback((contactId: number): void => {
    const newContactAddressItemId: number = StructureService.getNewItemId(
      [],
      (contactAddressNewItems && contactAddressNewItems[contactId]
        && Object.values(contactAddressNewItems[contactId])) || [],
    );
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);
    const formPrefix: string = `contactListData[${formKeyContact}]`;
    const formKeyContactAddressItem: string = FormService.getIndexedFormFieldKey(newContactAddressItemId);

    const newContactAddressItem: ContactAddressInput = {
      id: newContactAddressItemId,
      address: null,
      addressDetails: null,
      name: null,
    };

    if (newContactAddressItem.id) {
      setContactAddressNewItems({
        [contactId]: {
          ...(contactAddressNewItems[contactId] || {}),
          [newContactAddressItem.id]: newContactAddressItem,
        },
      });
    }

    changeForm(`${formPrefix}.addresses[${formKeyContactAddressItem}]`, newContactAddressItem);
  }, [contactAddressNewItems]);

  const onContactAddressDeleteItemClick: Function = useCallback((
    contactId: number, contactAddressItemId: number,
  ): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);
    const formPrefix: string = `contactListData[${formKeyContact}]`;
    const formKeyContactAddressItem: string = FormService.getIndexedFormFieldKey(contactAddressItemId);

    if (contactAddressNewItems && contactAddressNewItems[contactId]) {
      const newContactAddressItems: Record<string, ContactAddressInput> = { ...contactAddressNewItems[contactId] };
      delete newContactAddressItems[contactAddressItemId];
      setContactAddressNewItems({
        [contactId]: newContactAddressItems,
      });
    }

    changeForm(`${formPrefix}.addresses[${formKeyContactAddressItem}]`, null);
  }, [contactAddressNewItems]);

  const onContactAddressDetailsOpen: Function = useCallback((contactId: number, contactAddressId: number): void => {
    if (contactAddressDetailsItems) {
      const contactAddressDetailsIdIndex: number = (contactAddressDetailsItems[contactId] || [])
        .indexOf(contactAddressId);

      if (contactAddressDetailsIdIndex === FormatService.INDEX_NOT_FOUND) {
        setContactAddressDetailsItems({
          [contactId]: [
            ...(contactAddressDetailsItems[contactId] || []),
            contactAddressId,
          ],
        });
      }
    }
  }, [contactAddressDetailsItems]);

  const onContactAddressDetailsClose: Function = useCallback((contactId: number, contactAddressId: number): void => {
    if (contactAddressDetailsItems && contactAddressDetailsItems[contactId]) {
      const contactAddressDetailsIdIndex: number = contactAddressDetailsItems[contactId].indexOf(contactAddressId);

      if (contactAddressDetailsIdIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(contactAddressDetailsItems[contactId], contactAddressDetailsIdIndex);
      }

      setContactAddressDetailsItems({
        [contactId]: [...contactAddressDetailsItems[contactId]],
      });
    }
  }, [contactAddressDetailsItems]);

  const onCancelForm: Function = useCallback((): void => {
    if (inlineSaveContactId !== null) {
      if (isContactModalOpen) {
        closeContactModal();
      }

      inlineCancel();
    } else {
      if (isContactModalOpen) {
        closeContactModal();
      }

      FormService.forceResetForm(resetForm, formProps);

      setContactPhotoFiles({});
      setMainContactAddressList(null);
      setContactAddressesList(null);
      setSelectedRows([]);
      setAllRowsSelected(false);
      setContactActiveIds([]);
      setContactPhotoPreviewUrl(null);
      setContactAddressNewItems({});
      setContactAddressDetailsItems({});

      if (!isNewContact) {
        dispatch(toggleEditMode(false));
        dispatch(toggleExportMode(false));
        dispatch(toggleSelectMode(false));
        dispatch(toggleCustomizeMode(false));
      }

      dispatch(resetContactCompanyData());
      dispatch(resetContactPersonData());
    }
  }, [
    isNewContact, inlineSaveContactId, contactActiveIds, isEditMode, initialFormValues, contactPhotoFiles,
    mainContactAddressList, contactAddressesList,
  ]);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (!isNewContact) {
      if (isEditMode) {
        dispatch(toggleEditMode(false));
      }
      if (isExportMode) {
        dispatch(toggleExportMode(false));
      }
      if (isSelectMode) {
        dispatch(toggleSelectMode(false));
      }
      if (isCustomizeMode) {
        dispatch(toggleCustomizeMode(false));
      }
    }

    return true;
  }, [isFormChanged, isNewContact, isEditMode, isExportMode, isSelectMode, isCustomizeMode]);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, [isNewContact, inlineSaveContactId, contactActiveIds, isEditMode, initialFormValues, contactPhotoFiles]);

  const onPageChange: ChangePageHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent> | null,
    pageValue: number,
  ): void => {
    setPage(pageValue);

    ScreenService.resetScrollPosition();

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    queryParams[QUERY_PARAM_PAGE] = pageValue + 1;

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        if (valueItem) {
          const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        }
      });
    }

    navigate({
      pathname: ROUTE_CONTACT_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, queryTab, queryLimit, filters, page, limit]);

  const onLimitChange: Function = useCallback((limitValue: number): void => {
    setPage(PAGE_DEFAULT);
    setLimit(limitValue);

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    queryParams[QUERY_PARAM_LIMIT] = limitValue;

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        if (valueItem) {
          const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        }
      });
    }

    navigate({
      pathname: ROUTE_CONTACT_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, queryTab, filters, page, limit]);

  const openDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    setContactActiveIds(contactId ? [contactId] : selectedRows);
    setIsDeleteConfirmModalOpen(true);
  }, [selectedRows]);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setContactActiveIds([]);
    setConfirmModalErrorMessage(null);
  }, []);

  const getOptions: Function = useCallback((
    pageOption: number, limitOption: number, filtersOption?: Record<string, FilterValue>, sortOption?: SortParams,
  ): RequestOptions => {
    let options: RequestOptions = {
      page: pageOption + 1,
      limit: limitOption,
      filters: selectedContactGroup ? { ...filtersOption, group: selectedContactGroup } : filtersOption,
    };

    options = sortOption ? { ...options, sort: sortOption } : options;

    return options;
  }, [selectedContactGroup]);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!contactActiveIds || !contactActiveIds.length) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteContactList({
        contactIds: contactActiveIds,
        isListItem: true,
        ...getOptions(page, limit, filters, sortParams),
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    contactActiveIds, page, limit, filters, sortParams, selectedContactGroup,
    isNewContact, inlineSaveContactId, isEditMode, initialFormValues, contactPhotoFiles,
  ]);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: CONTACT_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, contactId: number): void => {
        openDeleteConfirmModal(event, contactId);
      },
    },
  ], [selectedRows]);

  const onSelectAllRows: Function = useCallback((event: React.ChangeEvent): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;
    setSelectedRows(isSelected ? contactListData.map((contactItem: Contact): number => contactItem.id) : []);
    setAllRowsSelected(!withAllRowsSelected);
  }, [contactListData, withAllRowsSelected]);

  const onSelectRow: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    rowId: number,
  ): void => {
    setSelectedRows(StructureService.selectRow(selectedRows, rowId));
    setAllRowsSelected(false);

    event.stopPropagation();
  }, [selectedRows]);

  const checkIsRowSelected: Function = useCallback((rowId: number): boolean => (
    selectedRows ? selectedRows.includes(rowId) : false
  ), [selectedRows]);

  const onAddressSearchCompleted: Function = useCallback((
    addressObject: JsonObject, contactId: number, contactAddressId?: number,
  ): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);
    const formKeyContactAddress: string | null = contactAddressId
      ? FormService.getIndexedFormFieldKey(contactAddressId) : null;
    const formPrefix: string = `contactListData[${formKeyContact}]${formKeyContactAddress
      ? `.addresses[${formKeyContactAddress}]` : ''}`;

    if (contactAddressId) {
      setContactAddressesList({
        ...(contactAddressesList || {}),
        [contactId]: {
          ...((contactAddressesList && contactAddressesList[contactId]) || {}),
          [contactAddressId]: { ...addressObject },
        },
      });
    } else {
      setMainContactAddressList({
        ...(mainContactAddressList || {}),
        [contactId]: { ...addressObject },
      });
    }

    changeForm(`${formPrefix}.address`, addressObject);
    changeForm(`${formPrefix}.location`, GoogleMapService.extractLocation(addressObject));
    changeForm(`${formPrefix}.postalCode`, GoogleMapService.extractPostalCode(addressObject));
    changeForm(`${formPrefix}.streetAddress`, GoogleMapService.extractStreetAddress(addressObject));
  }, [mainContactAddressList, contactAddressesList]);

  const onAddressFieldKeyDown: Function = useCallback((contactId: number, contactAddressId?: number): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);
    const formKeyContactAddress: string | null = contactAddressId
      ? FormService.getIndexedFormFieldKey(contactAddressId) : null;
    const formPrefix: string = `contactListData[${formKeyContact}]${formKeyContactAddress
      ? `.addresses[${formKeyContactAddress}]` : ''}`;

    if ((contactAddressId && contactAddressesList && contactAddressesList[contactId]
        && contactAddressesList[contactId][contactAddressId]) ||
      (mainContactAddressList && mainContactAddressList[contactId])) {
      if (contactAddressId) {
        setContactAddressesList({
          ...(contactAddressesList || {}),
          [contactId]: {
            ...((contactAddressesList && contactAddressesList[contactId]) || {}),
            [contactAddressId]: null,
          },
        });
      } else {
        setMainContactAddressList({ ...mainContactAddressList, [contactId]: null });
      }

      changeForm(`${formPrefix}.address`, '');
      changeForm(`${formPrefix}.location`, '');
      changeForm(`${formPrefix}.postalCode`, '');
      changeForm(`${formPrefix}.streetAddress`, '');
    }
  }, [mainContactAddressList, contactAddressesList]);

  const onTagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagId: number,
  ): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAG_ID]: tagId,
    };

    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const onLongClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    if (!isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode) {
      dispatch(toggleSelectMode(true));
      onSelectRow(event, contactId);
    }
  }, [selectedRows, isCustomizeMode, isExportMode, isSelectMode, isEditMode]);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const onActionCellClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const setFilterValue: Function = useCallback((filterType: string, value: string): Record<string, FilterValue> => {
    const selectedValue: FilterValue = FormatService.parseInt(value) !== FILTER_VALUE_EMPTY ? value : null;
    const newFilters: Record<string, FilterValue> = { ...currentFilters, [filterType]: selectedValue };

    if (filterType === FILTER_TYPE_CONTACT_TYPE && value !== CONTACT_TYPE_PERSON) {
      newFilters[FILTER_TYPE_COMPANY_ID] = null;
    }

    setCurrentFilters({ ...newFilters });

    return newFilters;
  }, [currentFilters]);

  const onResetFilters: Function = useCallback((): void => {
    setCurrentFilters({ ...filters });
  }, [filters]);

  const onApplyFilters: Function = useCallback((newFilters: Record<string, FilterValue>): void => {
    const resultNewFilters: Record<string, FilterValue> = newFilters || currentFilters;

    setFilters({ ...resultNewFilters });
    setPage(PAGE_DEFAULT);

    dispatch(getContactListData(getOptions(PAGE_DEFAULT, limit, resultNewFilters, sortParams)));

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    if (resultNewFilters) {
      Object.entries(resultNewFilters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    navigate({
      pathname: ROUTE_CONTACT_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, currentFilters, limit, sortParams, queryTab, queryLimit, selectedContactGroup]);

  const onDeleteFilterValue: Function = useCallback((filterType: string): void => {
    const newFilters: Record<string, FilterValue> = setFilterValue(filterType, null);

    onApplyFilters(newFilters);
  }, [locationQueryParams, currentFilters, limit, sortParams, queryTab, queryLimit, selectedContactGroup]);

  const onSetQuickFilterValue: Function = useCallback((filterType: string, filterValue: FilterValue): void => {
    const newFilters: Record<string, FilterValue> = setFilterValue(filterType, filterValue);

    onApplyFilters(newFilters);
  }, [locationQueryParams, currentFilters, limit, sortParams, queryTab, queryLimit, selectedContactGroup]);

  const onExportSuccess: Function = useCallback((): void => {
    onCancelForm();

    setStatusMessage('Exported successfully');
    setIsOpenMessage(true);
  }, [isNewContact, inlineSaveContactId, contactActiveIds, isEditMode, initialFormValues, contactPhotoFiles]);

  const onImportSuccess: Function = useCallback((): void => {
    setPage(PAGE_DEFAULT);

    dispatch(getContactListData(getOptions(PAGE_DEFAULT, limit, filters, sortParams)));
  }, [limit, filters, sortParams, selectedContactGroup]);

  const openExportModal: Function = useCallback((): void => {
    if (selectedRows) {
      setContactActiveIds(selectedRows);
      setIsExportModalOpen(true);
    }
  }, [selectedRows]);

  const closeExportModal: Function = useCallback((): void => {
    setIsExportModalOpen(false);

    onCancelForm();
    closeMessage();
  }, [isNewContact, inlineSaveContactId, contactActiveIds, isEditMode, initialFormValues, contactPhotoFiles]);

  const populateContactData: Function = useCallback((contactData: Contact): void => {
    const contactId: number = contactData.id;
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);
    const formPrefix: string = `contactListData[${formKeyContact}]`;

    if (contactData.firstName) {
      changeForm(`${formPrefix}.firstName`, contactData.firstName);
    }

    if (contactData.lastName) {
      changeForm(`${formPrefix}.lastName`, contactData.lastName);
    }

    if (contactData.name) {
      changeForm(`${formPrefix}.name`, contactData.name);
    }

    if (contactData.photo) {
      changeForm(`${formPrefix}.photoLink`, contactData.photo);
      setContactPhotoPreviewUrl(contactData.photo);
    }

    if (contactData.phone) {
      changeForm(`${formPrefix}.phone`, contactData.phone);
      changeForm(`${formPrefix}.phoneExtension`, contactData.phoneExtension || null);
    }

    if (contactData.phoneWork) {
      changeForm(`${formPrefix}.phoneWork`, contactData.phoneWork);
      changeForm(`${formPrefix}.phoneWorkExtension`, contactData.phoneWorkExtension || null);
    }

    if (contactData.address) {
      changeForm(`${formPrefix}.address`, contactData.address);
      changeForm(`${formPrefix}.location`, GoogleMapService.extractLocation(contactData.address));
      changeForm(`${formPrefix}.postalCode`, GoogleMapService.extractPostalCode(contactData.address));
      changeForm(`${formPrefix}.streetAddress`, GoogleMapService.extractStreetAddress(contactData.address));
    }

    if (contactData.linkedin) {
      changeForm(`${formPrefix}.linkedin`, contactData.linkedin);
    }

    if (contactData.description) {
      changeForm(`${formPrefix}.description`, contactData.description);
    }

    if (contactData.title) {
      changeForm(`${formPrefix}.title`, contactData.title);
    }

    if (contactData.industry) {
      changeForm(`${formPrefix}.industry`, contactData.industry);
    }

    if (contactData.foundationYear) {
      changeForm(`${formPrefix}.foundationYear`, contactData.foundationYear);
    }
  }, []);

  const onContactWebsiteChange: Function = useCallback((contactId: number, website: string): void => {
    const domain: string | null = (FormatService.isValidDomain(website) && website)
      || (FormatService.isValidLink(website) ? FormatService.extractWebsiteDomain(website) : null);

    if (!domain) {
      return;
    }

    dispatch(getContactCompanyByDomainData({
      domain,
      contactId,
    }));
  }, [contactCompanyData]);

  const onContactEmailChange: Function = useCallback((contactId: number, email: string): void => {
    if (!email || !FormatService.isValidEmail(email)) {
      return;
    }

    dispatch(getContactPersonByEmailData({
      email,
      contactId,
    }));
  }, [contactPersonData]);

  const onContactTagsChange: Function = useCallback((contactId: number, tags: Tag[]): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);

    changeForm(`contactListData[${formKeyContact}].tags`, tags);
  }, []);

  const onContactGroupsChange: Function = useCallback((contactId: number, groups: ContactGroup[]): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);

    changeForm(`contactListData[${formKeyContact}].groupIds`, groups);
  }, []);

  const onAssetTypesChange: Function = useCallback((contactId: number, assetTypeValues: AssetType[]): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);

    changeForm(`contactListData[${formKeyContact}].assetTypes`, assetTypeValues);
  }, []);

  const onContactPhoneChange: Function = useCallback((contactId: number, phoneValue?: string): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);

    changeForm(`contactListData[${formKeyContact}].phone`, phoneValue);
  }, []);

  const onContactPhoneWorkChange: Function = useCallback((contactId: number, phoneWorkValue?: string): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);

    changeForm(`contactListData[${formKeyContact}].phoneWork`, phoneWorkValue);
  }, []);

  const onContactPhotoChange: Function = useCallback((contactId: number, file: File): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);

    setContactPhotoFiles({ ...contactPhotoFiles, [contactId]: file });
    changeForm(`contactListData[${formKeyContact}].photo`, file);
  }, [contactPhotoFiles]);

  const onContactPhotoDelete: Function = useCallback((contactId: number): void => {
    const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);

    const newContactPhotoFiles: Record<string, File> = { ...contactPhotoFiles };
    delete newContactPhotoFiles[contactId];
    setContactPhotoFiles(newContactPhotoFiles);

    changeForm(`contactListData[${formKeyContact}].photo`, null);
  }, [contactPhotoFiles]);

  const removeCreateQueryParam: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    delete queryParams[QUERY_PARAM_CREATE];

    navigate({
      pathname: ROUTE_CONTACT_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams]);

  const openContactModal: Function = useCallback((isNew?: boolean): void => {
    setIsContactModalOpen(true);

    if (isNew) {
      setIsNewContact(isNew);
    }

    if (isEditMode) {
      setInlineSaveContactId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(null);
    }
  }, [isEditMode]);

  const closeContactModal: Function = useCallback((): void => {
    setIsContactModalOpen(false);
    setIsNewContact(false);

    if (isEditMode) {
      setInlineSaveContactId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    }
  }, [isEditMode, contactActiveIds]);

  const checkIsContactActive: Function = useCallback((contactId: number): boolean => (
    contactActiveIds ? contactActiveIds.includes(contactId) : false
  ), [contactActiveIds]);

  const inlineCancel: Function = useCallback((): void => {
    const contactId: number | null = inlineSaveContactId;

    if (contactId) {
      const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);

      if (initialFormValues && initialFormValues.contactListData) {
        changeForm(`contactListData[${formKeyContact}]`, initialFormValues.contactListData[formKeyContact]);
      }

      if (contactActiveIds && contactId !== FormService.FORM_KEY_NEW) {
        const contactActiveIdIndex: number = contactActiveIds.findIndex((contactItemActiveId: number): boolean => (
          contactItemActiveId === contactId
        ));

        if (contactActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
          const newContactActiveIds: number[] = [...contactActiveIds];
          StructureService.deleteListItemByIndex(newContactActiveIds, contactActiveIdIndex);
          setContactActiveIds(newContactActiveIds);
        }
      }

      if (contactPhotoFiles[contactId]) {
        const newContactPhotoFiles: Record<string, File> = { ...contactPhotoFiles };
        delete newContactPhotoFiles[contactId];
        setContactPhotoFiles(newContactPhotoFiles);
      }

      if (mainContactAddressList && mainContactAddressList[contactId]) {
        const newContactAddressList: Record<string, JsonObject> = { ...mainContactAddressList };
        delete newContactAddressList[contactId];
        setMainContactAddressList(newContactAddressList);
      }
    }

    setInlineSaveIsConfirm(null);
    setInlineSaveContactId(null);
  }, [initialFormValues, contactActiveIds, contactPhotoFiles, mainContactAddressList, inlineSaveContactId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    let formData: Record<string, FormValueItem> = {};

    if (isNewContact) {
      if (values.contactListData[formKeyNew]) {
        formData[formKeyNew] = {
          ...values.contactListData[formKeyNew],
        };
      }
    } else {
      formData = {
        ...values.contactListData,
      };
    }

    if (!Object.keys(formData).length) {
      return null;
    }

    const contactList: ContactInput[] = [];
    Object.keys(formData).forEach((contactIndex: string): void => {
      const contactFormItem: FormValueItem = {
        ...formData[contactIndex],
      };

      const contactId: number = isNewContact ? FormService.FORM_KEY_NEW : contactFormItem.id;

      if (contactFormItem && (isNewContact || (inlineSaveContactId && inlineSaveContactId === contactFormItem.id)
        || (!inlineSaveContactId && checkIsContactActive(contactFormItem.id)))) {
        delete contactFormItem.location;
        delete contactFormItem.streetAddress;
        delete contactFormItem.postalCode;

        contactFormItem.userId = contactFormItem.userId || null;
        contactFormItem.companyId = contactFormItem.companyId || null;
        contactFormItem.foundationYear = contactFormItem.foundationYear
          ? FormatService.parseInt(contactFormItem.foundationYear) : null;
        contactFormItem.phone = contactFormItem.phone && FormatService.isValidPhoneNumber(contactFormItem.phone, true)
          ? FormatService.formatPhoneValue(contactFormItem.phone) : null;
        contactFormItem.phoneExtension = contactFormItem.phoneExtension
          ? FormatService.parseInt(contactFormItem.phoneExtension) : null;
        contactFormItem.phoneWork = contactFormItem.phoneWork
        && FormatService.isValidPhoneNumber(contactFormItem.phoneWork, true)
          ? FormatService.formatPhoneValue(contactFormItem.phoneWork) : null;
        contactFormItem.phoneWorkExtension = contactFormItem.phoneWorkExtension
          ? FormatService.parseInt(contactFormItem.phoneWorkExtension) : null;
        contactFormItem.groupIds = contactFormItem.groupIds && contactFormItem.groupIds.length
          ? contactFormItem.groupIds.map((contactGroupItem: ContactGroup): number => contactGroupItem.id) : [];
        contactFormItem.tags = contactFormItem.tags && contactFormItem.tags.length
          ? contactFormItem.tags.map((tagItem: Tag): string => tagItem.name) : [];
        contactFormItem.assetTypes = contactFormItem.assetTypes && contactFormItem.assetTypes.length
          ? contactFormItem.assetTypes.map((assetTypeItem: AssetType): number => assetTypeItem.id) : [];

        if (contactPhotoFiles[contactId]) {
          contactFormItem.photo = contactPhotoFiles[contactId];
        } else {
          delete contactFormItem.photo;
        }

        const contactAddressItems: ContactAddressInput[] = [];
        if (contactFormItem.addresses && Object.keys(contactFormItem.addresses).length) {
          Object.keys(contactFormItem.addresses).forEach((contactAddressIndex: string): void => {
            const contactAddressItem: FormValueItem = { ...contactFormItem.addresses[contactAddressIndex] };

            if (contactAddressItem && contactAddressItem.id) {
              delete contactAddressItem.location;
              delete contactAddressItem.streetAddress;
              delete contactAddressItem.postalCode;

              if (!contactAddressItem.addressDetails) {
                contactAddressItem.addressDetails = null;
              }

              delete contactAddressItem.id;

              contactAddressItems.push(contactAddressItem);
            }
          });
        }
        contactFormItem.addresses = contactAddressItems;

        const contactItem: ContactInput = contactFormItem;

        contactList.push(contactItem);
      }
    });

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveContactListData({
        contactListForm: {
          contactList,
        },
        ...getOptions(page, limit, filters, sortParams),
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        setInlineSaveContactId(null);
        setInlineSaveIsConfirm(null);

        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    isSaveDisabled, isNewContact, contactPhotoFiles, page, limit, filters, sortParams, isContactModalOpen,
    selectedContactGroup, inlineSaveContactId, contactActiveIds, isEditMode, initialFormValues, formKeyNew,
  ]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [
    isSaveDisabled, isNewContact, contactPhotoFiles, page, limit, filters, sortParams, isContactModalOpen,
    selectedContactGroup, inlineSaveContactId, contactActiveIds, isEditMode, initialFormValues, formKeyNew,
    formValues, withFormErrors,
  ]);

  const onInlineSave: Function = useCallback((contactId: number): void => {
    setInlineSaveContactId(contactId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onInlineCancel: Function = useCallback((contactId: number): void => {
    setInlineSaveContactId(contactId);
    setInlineSaveIsConfirm(false);
  }, []);

  const onCreateButtonClick: Function = useCallback((): void => {
    openContactModal(true);
  }, [isEditMode]);

  const onContactCreate: Function = useCallback((onSuccess?: Function): void => {
    if (isEditMode) {
      setInlineSaveContactId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(true);
    } else {
      onSubmitForm();

      if (onSuccess) {
        onSuccess();
      }
    }
  }, [
    isEditMode, isSaveDisabled, isNewContact, contactPhotoFiles, page, limit, filters, sortParams, isContactModalOpen,
    initialFormValues, inlineSaveContactId, contactActiveIds, formKeyNew, formValues, withFormErrors,
  ]);

  const onContactModalCancel: Function = useCallback((): void => {
    if (isEditMode) {
      setInlineSaveContactId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    } else {
      onCancelForm();
    }
  }, [isEditMode, isNewContact, inlineSaveContactId, contactActiveIds, initialFormValues, contactPhotoFiles]);

  const onContactSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const onBeforeTabChange: Function = useCallback((newTabIndex: number): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastTabIndex(newTabIndex);
    }
  }, [isFormChanged]);

  const sortTableRows: Function = useCallback((columnName: string): void => {
    const sortParamsValue: SortParams = {
      field: columnName,
      type: sortParams.field === columnName && sortParams.type === SORT_TYPE_DESC ? SORT_TYPE_ASC : SORT_TYPE_DESC,
    };

    setPage(PAGE_DEFAULT);
    setSortParams(sortParamsValue);

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    if (queryLimit) {
      queryParams[QUERY_PARAM_LIMIT] = queryLimit;
    }

    if (filters) {
      Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    navigate({
      pathname: ROUTE_CONTACT_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, sortParams, queryTab, queryLimit, filters]);

  const onRowClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
    isListView?: boolean,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection && !isCustomizeMode) {
      if (isExportMode || isSelectMode) {
        onSelectRow(event, contactId);
      } else if (isEditMode && isListView) {
        const queryParams: LocationQueryParams = {
          [QUERY_PARAM_EDIT_MODE]: isEditMode,
        };

        if (contactId) {
          navigate({
            pathname: `${ROUTE_CONTACT}/${contactId}`,
            search: FormatService.formatQueryParams(queryParams),
          });
        }
      } else if (!isEditMode && contactId) {
        navigate({
          pathname: `${ROUTE_CONTACT}/${contactId}`,
        });
      }
    }
  }, [selectedRows, isCustomizeMode, isExportMode, isSelectMode, isEditMode]);

  const onInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, contactId: number,
  ): void => {
    setContactActiveIds([...(contactActiveIds || []), contactId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, contactActiveIds]);

  const tableContactListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'photo',
      title: 'Photo',
      group: 'default',
      isSortable: false,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCellShort isTextCenter>
          {
            (!!contact.photoThumbnail || !!contact.photo) && (
              <PaddedFlexContainer isCentered>
                <Image text={contact.fullName} link={contact.photoThumbnail || contact.photo} />
              </PaddedFlexContainer>
            )
          }
          {
            (!contact.photoThumbnail && !contact.photo) && (
              <LetterAvatar
                wordList={contact.type && contact.type === CONTACT_TYPE_COMPANY
                  ? [contact.name] : [contact.firstName, contact.lastName]}
              />
            )
          }
        </TableCellShort>
      ),
    },
    {
      id: 2,
      name: 'fullName',
      title: 'Full Name',
      group: 'default',
      isSortable: true,
      isNumber: false,
      isSticky: !isCustomizeMode,
      component: (contact: Contact): ReactElement => (
        <TableCell
          isText
          isSticky={!isCustomizeMode}
          isStickyLeft={!isCustomizeMode && !isExportMode && !isSelectMode}
          isStickyLeftSelect={!isCustomizeMode && (isExportMode || isSelectMode)}
        >
          <FilteredLink
            link={!isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && `${ROUTE_CONTACT}/${contact.id}`}
            permission={CONTACT_VIEW}
          >
            {contact.fullName}
          </FilteredLink>
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'name',
      title: 'Name',
      group: 'default',
      isRequired: isEditMode,
      isSortable: false,
      isNumber: false,
      isEditModeOnly: true,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_COMPANY : undefined,
      component: (contact: Contact): ReactElement | boolean => (
        ((filterContactType && filterContactType === CONTACT_TYPE_COMPANY) || !filterContactType || isEditMode) && (
          <TableCell>
            {contact.type === CONTACT_TYPE_COMPANY ? contact.name : '-'}
          </TableCell>
        )
      ),
    },
    {
      id: 4,
      name: 'firstName',
      title: 'First Name',
      group: 'default',
      isRequired: isEditMode,
      isSortable: false,
      isNumber: false,
      isEditModeOnly: true,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_PERSON : undefined,
      component: (contact: Contact): ReactElement | boolean => (
        ((filterContactType && filterContactType === CONTACT_TYPE_PERSON) || !filterContactType || isEditMode) && (
          <TableCell>
            {contact.type === CONTACT_TYPE_PERSON ? contact.firstName : '-'}
          </TableCell>
        )
      ),
    },
    {
      id: 5,
      name: 'lastName',
      title: 'Last Name',
      group: 'default',
      isRequired: isEditMode,
      isSortable: false,
      isNumber: false,
      isEditModeOnly: true,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_PERSON : undefined,
      component: (contact: Contact): ReactElement | boolean => (
        ((filterContactType && filterContactType === CONTACT_TYPE_PERSON) || !filterContactType || isEditMode) && (
          <TableCell>
            {contact.type === CONTACT_TYPE_PERSON ? contact.lastName : '-'}
          </TableCell>
        )
      ),
    },
    {
      id: 6,
      name: 'type.title',
      title: 'Type',
      group: 'default',
      isRequired: isEditMode,
      isSortable: true,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {contact.typeText}
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'company.name',
      title: 'Company',
      group: 'default',
      isSortable: true,
      isNumber: false,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_PERSON : undefined,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {
            contact.company && (
              <ChipLink
                isDisabled={isEditMode || isCustomizeMode || isExportMode || isSelectMode}
                label={contact.company.name}
                imageLink={contact.company.photoThumbnail || contact.company.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  if (contact.company) {
                    onContactClick(event, contact.company.id);
                  }
                }}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'group.title',
      title: 'Groups',
      group: 'default',
      isRequired: isEditMode,
      isSortable: false,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {
            contact.groups && !!contact.groups.length
            && contact.groups.map((contactGroupItem: ContactGroup): ReactElement => (
              <Chip
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={contactGroupItem.id}
                label={contactGroupItem.title}
              />
            ))
          }
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'phone',
      title: 'Phone',
      group: 'default',
      isSortable: true,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {
            contact.phone && contact.phoneText && (
              <FilteredLink
                isPhone
                link={!isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode
                  && FormatService.formatCleanIntegerNumberString(contact.phone)}
                phoneExtension={contact.phoneExtension}
              >
                {`${contact.phoneText}${contact.phoneExtension ? ` ext. ${contact.phoneExtension}` : ''}`}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 10,
      name: 'phoneWork',
      title: 'Work Phone',
      group: 'default',
      isSortable: true,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {
            contact.phoneWork && contact.phoneWorkText && (
              <FilteredLink
                isPhone
                link={!isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode
                  && FormatService.formatCleanIntegerNumberString(contact.phoneWork)}
                phoneExtension={contact.phoneWorkExtension}
              >
                {`${contact.phoneWorkText}${contact.phoneWorkExtension ? ` ext. ${contact.phoneWorkExtension}` : ''}`}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'email',
      title: 'Email',
      group: 'default',
      isSortable: true,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell>
          {
            contact.email && (
              <TextNoWrapBlock>
                <Tooltip title={contact.email}>
                  <FilteredLink
                    isEmail
                    link={!isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && contact.email}
                  >
                    {contact.email}
                  </FilteredLink>
                </Tooltip>
              </TextNoWrapBlock>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 12,
      name: 'streetAddress',
      title: 'Address',
      group: 'default',
      isSortable: false,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {
            contact.streetAddress && (
              <FilteredLink
                isExternal
                link={!isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode
                  && contact.address && contact.address.url}
              >
                {contact.streetAddress}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 13,
      name: 'addressDetails',
      title: 'Address Details',
      group: 'default',
      isSortable: false,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {
            contact.addressDetails
              ? FormatService.formatLimitedString(contact.addressDetails, FormatService.TEXT_LIMIT_TABLE) : ''
          }
        </TableCell>
      ),
    },
    {
      id: 14,
      name: 'linkedin',
      title: 'LinkedIn',
      group: 'default',
      isSortable: false,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell isText isTextCenter>
          {
            contact.linkedin && (
              <FilteredLink
                isExternal
                link={!isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && contact.linkedin}
              >
                <LinkedinIcon />
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 15,
      name: 'title',
      title: 'Title',
      group: 'default',
      isSortable: false,
      isNumber: false,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_PERSON : undefined,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {contact.title}
        </TableCell>
      ),
    },
    {
      id: 16,
      name: 'description',
      title: 'Description',
      group: 'default',
      isSortable: false,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {
            contact.description
              ? FormatService.formatLimitedString(contact.description, FormatService.TEXT_LIMIT_TABLE) : ''
          }
        </TableCell>
      ),
    },
    {
      id: 17,
      name: 'industry',
      title: 'Industry',
      group: 'default',
      isSortable: false,
      isNumber: false,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_COMPANY : undefined,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {contact.industry}
        </TableCell>
      ),
    },
    {
      id: 18,
      name: 'foundationYear',
      title: 'Foundation Year',
      group: 'default',
      isSortable: false,
      isNumber: true,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_COMPANY : undefined,
      component: (contact: Contact): ReactElement | boolean => (
        (filterContactType && filterContactType === CONTACT_TYPE_COMPANY) || !filterContactType
      ) && (
        <TableCell isText>
          {contact.foundationYear}
        </TableCell>
      ),
    },
    {
      id: 19,
      name: 'headQuarter',
      title: 'Head Quarter',
      group: 'default',
      isSortable: false,
      isNumber: false,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_COMPANY : undefined,
      component: (contact: Contact): ReactElement | boolean => (
        ((filterContactType && filterContactType === CONTACT_TYPE_COMPANY) || !filterContactType) && (
          <TableCell isText>
            {contact.headQuarter}
          </TableCell>
        )
      ),
    },
    {
      id: 20,
      name: 'website',
      title: 'Website',
      group: 'default',
      isSortable: false,
      isNumber: false,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_COMPANY : undefined,
      component: (contact: Contact): ReactElement | boolean => (
        (filterContactType && filterContactType === CONTACT_TYPE_COMPANY) || !filterContactType
      ) && (
        <TableCell isText>
          {contact.website}
        </TableCell>
      ),
    },
    {
      id: 21,
      name: 'keySubsidiaries',
      title: 'Key Subsidiaries',
      group: 'default',
      isSortable: false,
      isNumber: false,
      isVisibleByType: !isEditMode ? CONTACT_TYPE_COMPANY : undefined,
      component: (contact: Contact): ReactElement | boolean => (
        (filterContactType && filterContactType === CONTACT_TYPE_COMPANY) || !filterContactType
      ) && (
        <TableCell isText>
          {contact.keySubsidiaries}
        </TableCell>
      ),
    },
    {
      id: 22,
      name: 'tags',
      title: 'Tags',
      group: 'default',
      isSortable: false,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell>
          {
            contact.tags && !!contact.tags.length && contact.tags.map((tagItem: Tag): ReactElement => (
              <ChipLink
                isDisabled={isEditMode || isCustomizeMode || isExportMode || isSelectMode}
                key={tagItem.id}
                label={tagItem.name}
                permission={SEARCH_RESULT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onTagClick(event, tagItem.id);
                }}
              />
            ))
          }
        </TableCell>
      ),
    },
    {
      id: 23,
      name: 'assetTypes',
      title: 'Asset Focus',
      group: 'default',
      isSortable: false,
      isNumber: false,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {
            contact.assetTypes && !!contact.assetTypes.length
            && contact.assetTypes.map((assetTypeItem: AssetType): ReactElement => (
              <Chip
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={assetTypeItem.id}
                label={assetTypeItem.title}
              />
            ))
          }
        </TableCell>
      ),
    },
    {
      id: 24,
      name: 'updates',
      title: 'Last Update',
      group: 'default',
      isSortable: false,
      isNumber: false,
      isHidden: isEditMode,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          <LastUpdateBlock
            withNoUpdates
            isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
            commentList={contact.comments}
            attachmentList={contact.attachments}
            commentListViewPermissions={contactCommentListViewPermissions}
            attachmentListViewPermissions={contactAttachmentListViewPermissions}
          />
        </TableCell>
      ),
    },
    {
      id: 25,
      name: 'relatedContacts',
      title: 'Related Contacts',
      group: 'default',
      isSortable: true,
      isNumber: false,
      isHidden: isEditMode,
      component: (contact: Contact): ReactElement => (
        <TableCell isText>
          {
            ((contact.type === CONTACT_TYPE_COMPANY && contact.relatedContacts && !!contact.relatedContacts.length)
              || (contact.type === CONTACT_TYPE_PERSON && contact.company)) && (
              <Fragment>
                {
                  contact.type === CONTACT_TYPE_COMPANY && contact.relatedContacts && !!contact.relatedContacts.length
                  && contact.relatedContacts.map((contactItem: Contact): ReactElement => (
                    <ChipLink
                      isDisabled={isEditMode || isCustomizeMode || isExportMode || isSelectMode}
                      key={contactItem.id}
                      label={contactItem.fullName}
                      imageLink={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                      permission={CONTACT_VIEW}
                      onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                        onContactClick(event, contactItem.id);
                      }}
                    />
                  ))
                }
                {
                  contact.type === CONTACT_TYPE_PERSON && contact.company && (
                    <ChipLink
                      isDisabled={isEditMode || isCustomizeMode || isExportMode || isSelectMode}
                      key={contact.company.id}
                      label={contact.company.name}
                      imageLink={contact.company.photoThumbnail || contact.company.photo || CompanyDefaultImage}
                      permission={CONTACT_VIEW}
                      onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                        if (contact.company) {
                          onContactClick(event, contact.company.id);
                        }
                      }}
                    />
                  )
                }
              </Fragment>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 26,
      name: 'actions',
      title: 'Actions',
      isSortable: false,
      isNumber: false,
      isHeaderNameHidden: true,
      isNotFiltered: true,
      isSticky: !isCustomizeMode && !isExportMode && !isSelectMode,
      isStickyRight: !isCustomizeMode && !isExportMode && !isSelectMode,
      component: (contact: Contact): ReactElement => (
        <TableCell
          isActions
          isTextRight
          isSticky={!isCustomizeMode && !isExportMode && !isSelectMode}
          isStickyRight={!isCustomizeMode && !isExportMode && !isSelectMode}
          onClick={onActionCellClick}
        >
          <CheckAccess
            permissions={actionMenuItems
              && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | undefined => actionMenuItem.permission)}
          >
            {
              !isExportMode && !isSelectMode && !isCustomizeMode
              && (!isEditMode || !checkIsContactActive(contact.id)) && (
                <CheckAccess permissions={contactEditPermissions}>
                  <ActionButton
                    withNoPadding
                    withFixedSize
                    isHidden={!!isFormSaving}
                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                      onInlineEditModeButtonClick(event, contact.id);
                    }}
                  >
                    <EditIcon />
                  </ActionButton>
                </CheckAccess>
              )
            }
            {
              !isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && (
                <ActionButton
                  withPopup
                  withNoPadding
                  withFixedSize
                  isHidden={!!isFormSaving || !!contact.user}
                  onClick={openActionMenu}
                  {...{
                    [DATA_CONTACT_ID]: contact.id,
                  }}
                >
                  <MoreIcon />
                </ActionButton>
              )
            }
          </CheckAccess>
        </TableCell>
      ),
    },
  ], [isCustomizeMode, isEditMode, isExportMode, isSelectMode, filterContactType, contactActiveIds, isFormSaving]);

  const tableContactListFieldGroups: TableFieldGroup[] = useMemo((): TableFieldGroup[] => [
    {
      id: 1,
      name: 'default',
      title: 'All Columns',
    },
  ], []);

  const filteredTableContactListFields: TableField[] = useMemo((): TableField[] => (
    tableContactListFields.filter((tableField: TableField): boolean => (
      !filters[FILTER_TYPE_CONTACT_TYPE] || (!!filters[FILTER_TYPE_CONTACT_TYPE]
        && (!tableField.isVisibleByType || (!!tableField.isVisibleByType
          && filters[FILTER_TYPE_CONTACT_TYPE] === tableField.isVisibleByType)))
    ))
  ), [tableContactListFields, filters]);

  const filterInvisibleColumns: Function = useCallback((tableColumnSettingItems: TableField[]): TableField[] => {
    const invisibleFieldNames: string[] = tableContactListFields
      .filter((tableField: TableField): boolean => !!tableField.isVisibleByType && !!filterContactType
        && tableField.isVisibleByType !== filterContactType).map((tableField: TableField): string => tableField.name);

    return tableColumnSettingItems.map((tableField: TableField): TableField => (
      invisibleFieldNames.includes(tableField.name) ? ({
        ...tableField,
        isVisible: false,
      }) : tableField
    )).filter((tableField: TableField): boolean => !!tableField);
  }, [tableContactListFields, filterContactType]);

  const onTableColumnSettingsChange: Function = useCallback((
    updatedTableColumnSettings: TableField[],
  ): void => {
    const filteredUpdatedTableColumnSettings: TableField[] = filterInvisibleColumns(updatedTableColumnSettings);

    if (filteredUpdatedTableColumnSettings && filteredUpdatedTableColumnSettings.length) {
      setTableColumnSettings(filteredUpdatedTableColumnSettings);

      dispatch(saveTableColumnSettings({
        tableName: TABLE_NAME_CONTACT_LIST,
        tableColumnSettings: filteredUpdatedTableColumnSettings,
      }));

      setExtendedTableFields(
        StructureService.extendOptions(
          tableContactListFields.filter((tableField): boolean => !tableField.isEditModeOnly),
          filteredUpdatedTableColumnSettings,
        ),
      );
    }
  }, [filterInvisibleColumns, tableContactListFields]);

  const onDropColumn: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    if (tableColumnSettings) {
      onTableColumnSettingsChange(StructureService.switchListItems(tableColumnSettings, dragIndex, hoverIndex));
    }
  }, [extendedTableFields, tableColumnSettings, ...tableColumnDropDeps]);

  const commonContactGroupList: ContactGroup[] = useMemo((): ContactGroup[] => [
    {
      id: ITEM_ID_CONTACT_GROUP_ALL,
      name: NAME_CONTACT_GROUP_ALL,
      title: 'All Contacts',
    },
    ...(contactGroupList || []),
  ], [contactGroupList]);

  const tabs: TabItem[] = commonContactGroupList
    && commonContactGroupList.map((contactGroupItem: ContactGroup): TabItem => ({
      id: contactGroupItem.id,
      name: contactGroupItem.name,
      title: contactGroupItem.title,
    }));

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        setSelectedContactGroup(tabItem.name !== NAME_CONTACT_GROUP_ALL ? tabItem.name : null);

        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        if (queryLimit) {
          queryParams[QUERY_PARAM_LIMIT] = queryLimit;
        }

        if (filters) {
          Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
            const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

            if (valueItem) {
              queryParams[filterUrlName] = encodeURIComponent(valueItem);
            } else {
              delete queryParams[filterUrlName];
            }
          });
        }

        navigate({
          pathname: ROUTE_CONTACT_LIST,
          search: FormatService.formatQueryParams(queryParams),
        });

        setPage(PAGE_DEFAULT);
      }
    });
  }, [
    locationQueryParams, isEditMode, tabs, queryLimit, filters, isNewContact, inlineSaveContactId, contactActiveIds,
    initialFormValues, contactPhotoFiles,
  ]);

  const applyUrlFilters: Function = useCallback((): Record<string, FilterValue> => {
    const filterValues: Record<string, FilterValue> = { ...filters };

    if (queryFilterContactType) {
      filterValues[FILTER_TYPE_CONTACT_TYPE] = queryFilterContactType;
    }

    if (queryFilterCompanyId) {
      filterValues[FILTER_TYPE_COMPANY_ID] = queryFilterCompanyId;
    }

    if (queryFilterLocation) {
      filterValues[FILTER_TYPE_LOCATION] = queryFilterLocation;
    }

    if (queryFilterAssetType) {
      filterValues[FILTER_TYPE_ASSET_TYPE] = queryFilterAssetType;
    }

    setFilters(filterValues);

    return filterValues;
  }, [filters, queryFilterContactType, queryFilterCompanyId, queryFilterLocation, queryFilterAssetType]);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getContactListData(getOptions(page, limit, filters, sortParams)));
  }, [page, limit, filters, sortParams]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = locationQueryParams || {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: ROUTE_CONTACT_LIST,
            search: FormatService.formatQueryParams(queryParams),
          });

          setTabIndex(lastTabIndex);
          setSelectedContactGroup(currentTab.name);
          setPage(PAGE_DEFAULT);
        }

        setLastTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
    }
  }, [lastLocation]);

  useEffect((): void => {
    if (currentUser && !tableColumnSettings) {
      const tableColumnSettingsCommon: Setting = currentUser.settings
        && currentUser.settings.find((settingItem: Setting): boolean => (
          settingItem.name === SETTING_NAME_TABLE_COLUMN_SETTINGS
        ));
      setTableColumnSettings(tableColumnSettingsCommon && tableColumnSettingsCommon.value
        && tableColumnSettingsCommon.value[TABLE_NAME_CONTACT_LIST]);
    }
  }, [currentUser]);

  useEffect((): void => {
    if (tableColumnSettings && tableColumnSettings.length && !extendedTableFields) {
      setExtendedTableFields(StructureService.extendOptions(tableContactListFields, tableColumnSettings));
    }
  }, [tableColumnSettings]);

  useEffect((): void => {
    if (extendedTableFields) {
      setExtendedTableFields(
        StructureService.extendOptions(tableContactListFields, tableColumnSettings, isEditMode),
      );
    }
  }, [isEditMode, isExportMode, isSelectMode, isCustomizeMode, contactActiveIds, isFormSaving]);

  useEffect((): void => {
    if (contactListData && !mainContactAddressList) {
      const initialContactAddressList: Record<string, JsonObject> = {};
      contactListData.forEach((contactItem: Contact): void => {
        initialContactAddressList[contactItem.id] = contactItem.address;
      });
      setMainContactAddressList(initialContactAddressList);
    }
  }, [contactListData]);

  useEffect((): void => {
    if (tabIndex === null) {
      setTabIndex(TAB_INDEX_DEFAULT);
    }
  }, [tabIndex]);

  useEffect((): void => {
    if (!isDataLoaded) {
      const currentPage: number = queryPage ? queryPage - 1 : page;
      if (queryPage) {
        setPage(queryPage - 1);
      }

      const currentLimit: number = queryLimit || limit;
      if (queryLimit) {
        setLimit(queryLimit);
      }

      const currentTab: string | null = queryTab && queryTab !== NAME_CONTACT_GROUP_ALL ? queryTab : null;
      const filterValues: Record<string, FilterValue> = applyUrlFilters();

      setSelectedContactGroup(currentTab);

      dispatch(getContactListData(
        getOptions(currentPage, currentLimit, { ...filterValues, group: currentTab }, sortParams),
      ));

      FormService.forceResetForm(resetForm, formProps);
    }
  }, [isDataLoaded, selectedContactGroup]);

  useEffect((): void => {
    if (isDataLoaded) {
      dispatch(getContactListData(getOptions(page, limit, filters, sortParams)));

      FormService.forceResetForm(resetForm, formProps);
    }
  }, [page, sortParams, selectedContactGroup]);

  useEffect((): void => {
    if (isDataLoaded) {
      const currentTab: string | null = queryTab && queryTab !== NAME_CONTACT_GROUP_ALL ? queryTab : null;

      dispatch(getContactListData(getOptions(PAGE_DEFAULT, limit, { ...filters, group: currentTab }, sortParams)));

      FormService.forceResetForm(resetForm, formProps);

      setPage(PAGE_DEFAULT);
      setFilters(DEFAULT_FILTERS);
      setSelectedContactGroup(currentTab);
    }
  }, [queryTab, limit, selectedContactGroup]);

  useEffect((): void => {
    if (inlineSaveIsConfirm !== null && inlineSaveContactId !== null) {
      if (inlineSaveIsConfirm) {
        onSubmitForm();
      } else {
        onCancelForm();
      }
    }
  }, [inlineSaveIsConfirm]);

  useEffect((): void => {
    if (queryCreate) {
      openContactModal(true);
      removeCreateQueryParam();
    }
  }, [queryCreate]);

  useEffect((): void => {
    if (contactCompanyData) {
      populateContactData(contactCompanyData);
    }
  }, [contactCompanyData]);

  useEffect((): void => {
    if (contactPersonData) {
      populateContactData(contactPersonData);
    }
  }, [contactPersonData]);

  useEffect((): void => {
    if (withServerErrors && inlineSaveIsConfirm !== null) {
      setInlineSaveContactId(null);
      setInlineSaveIsConfirm(null);
    }
  }, [withServerErrors]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useArrowNavigation(inputRefs);

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (!isDataLoaded || !tableColumnSettings) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={contactViewPermissions}>
      <Form id={FORM_ID_CONTACT_LIST} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <PageTabs
            isDisabled={isFormChanged}
            isCompressed={isRightDrawerOpen}
            tabs={tabs}
            currentTab={queryTab}
            subComponent={(
              <Fragment>
                <Controls
                  isTableExport
                  withFilters
                  withSelectAll
                  withSelectMode
                  withColumnSettings
                  withAllFiltersEmpty={withAllFiltersEmpty}
                  isSaveDisabled={isSaveDisabled}
                  isExportDisabled={isExportDisabled}
                  isCustomizable={isCustomizable}
                  isCustomizeMode={isCustomizeMode}
                  withCardView={!isEditMode || !isSmallScreen}
                  withAllRowsSelected={withAllRowsSelected}
                  exportFormatList={FORMAT_LIST_EXPORT_CONTACT}
                  importFormatList={FORMAT_LIST_IMPORT_CONTACT}
                  importExportEntityType={IMPORT_EXPORT_ENTITY_TYPE_CONTACT_LIST}
                  selectedItemIds={selectedRows}
                  createButtonTitle={createButtonTitle}
                  createPermission={CONTACT_CREATE}
                  editPermissions={contactEditPermissions}
                  importPermission={CONTACT_LIST_IMPORT}
                  exportPermission={CONTACT_LIST_EXPORT}
                  deletePermission={CONTACT_DELETE}
                  tableFields={filteredTableContactListFields}
                  tableFieldGroups={tableContactListFieldGroups}
                  filters={filters}
                  currentFilters={currentFilters}
                  tableColumnSettings={tableColumnSettings}
                  rowsTotal={contactsTotal}
                  rowsSelectedTotal={contactsSelectedTotal}
                  onCancelForm={onCancelForm}
                  onSubmitForm={onSubmitForm}
                  onExportClick={openExportModal}
                  onDeleteClick={openDeleteConfirmModal}
                  onSelectAllRows={onSelectAllRows}
                  onCreateButtonClick={onCreateButtonClick}
                  onImportSuccess={onImportSuccess}
                  onApplyFilters={onApplyFilters}
                  onResetFilters={onResetFilters}
                  onTableColumnsChange={onTableColumnSettingsChange}
                  filterComponent={(
                    <ContactListFilters
                      currentFilters={currentFilters}
                      filters={filters}
                      filterContactTypeList={filterContactTypeList}
                      filterContactCompanyList={filterContactCompanyList}
                      filterLocationList={filterLocationList}
                      filterAssetTypeList={filterAssetTypeList}
                      filterTagList={filterTagList}
                      onSetFilterValue={setFilterValue}
                    />
                  )}
                />
                {
                  !isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode && (
                    <FilterPanel
                      filters={filters}
                      filterOptionMap={FILTER_OPTION_MAP}
                      filterKeyMap={FILTER_KEY_MAP}
                      filterLabelMap={FILTER_LABEL_MAP}
                      filterLabelKeyMap={FILTER_LABEL_KEY_MAP}
                      quickFilters={quickFilters}
                      onSetQuickFilterValue={onSetQuickFilterValue}
                      onDeleteFilterValue={onDeleteFilterValue}
                    />
                  )
                }
              </Fragment>
            )}
            staticComponent={(
              <Fragment>
                {
                  !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode && contactLocationItemList
                  && !!contactLocationItemList.length && (
                    <ExpansionGroupWidget headerName="Contact Locations" permission={CONTACT_LIST_VIEW}>
                      <LocationMap
                        defaultColor={MARKER_COLOR_RED}
                        locationItemList={contactLocationItemList}
                      />
                    </ExpansionGroupWidget>
                  )
                }
                {
                  contactListData && !!contactListData.length
                  && extendedTableFields && !!extendedTableFields.length ? (
                    <Fragment>
                      {
                        isCardView ? (
                          <Grid isSpread isStretched>
                            {
                              contactListData.map((contact: Contact, contactIndex: number): ReactElement => {
                                const isSelected: boolean = selectedRows
                                  ? withAllRowsSelected || checkIsRowSelected(contact.id) : false;
                                const isActive: boolean = checkIsContactActive(contact.id);
                                const isInlineSaving: boolean = inlineSaveContactId !== null
                                  ? contact.id === inlineSaveContactId : isFormSaving;
                                const isCompressed: boolean = isEditMode || (isRightDrawerOpen && !isMediumScreen);
                                const isExtraCompressed: boolean = isEditMode && isRightDrawerOpen;

                                return (
                                  <GridItemCardViewBlock
                                    isCompressed={isCompressed}
                                    isExtraCompressed={isExtraCompressed}
                                    key={contact.id}
                                  >
                                    {
                                      isEditMode && isActive ? (
                                        <ContactRowForm
                                          isCardView
                                          isActive={isActive}
                                          isSaving={isFormSaving}
                                          isSaveDisabled={isSaveDisabled}
                                          isInlineSaving={isInlineSaving}
                                          inputRefs={inputRefs}
                                          contactIndex={contactIndex}
                                          contactData={contact}
                                          contactList={contactListData}
                                          tableColumnSettings={tableColumnSettings}
                                          fileDropDeps={fileDropDeps}
                                          currentUser={currentUser}
                                          tagList={tagList}
                                          contactGroupList={contactGroupList}
                                          contactTypeList={contactTypeList}
                                          contactCompanyList={contactCompanyList}
                                          assetTypeList={assetTypeList}
                                          onInlineSave={onInlineSave}
                                          onInlineCancel={onInlineCancel}
                                          onAssetTypesChange={onAssetTypesChange}
                                          onContactGroupsChange={onContactGroupsChange}
                                          onPhoneChange={onContactPhoneChange}
                                          onPhoneWorkChange={onContactPhoneWorkChange}
                                          onAddressSearchCompleted={onAddressSearchCompleted}
                                          onAddressFieldKeyDown={onAddressFieldKeyDown}
                                          onContactCompanySearch={onContactCompanySearch}
                                          onContactCompanySearchClose={onContactCompanySearchClose}
                                          onTagSearch={onTagSearch}
                                          onTagSearchClose={onTagSearchClose}
                                          onContactPhotoChange={onContactPhotoChange}
                                          onContactPhotoDelete={onContactPhotoDelete}
                                          onTagsChange={onContactTagsChange}
                                        />
                                      ) : (
                                        <LongClickCard
                                          isContainer
                                          isFullHeight
                                          isSelectable={isExportMode || isSelectMode}
                                          isActive={isSelected}
                                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                            onRowClick(event, contact.id, isSmallScreen);
                                          }}
                                          onLongClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                            onLongClick(event, contact.id);
                                          }}
                                        >
                                          <Grid>
                                            <GridItem isFullWidth>
                                              <FlexContainer isSpread>
                                                <FlexContainer>
                                                  {
                                                    (isExportMode || isSelectMode) ? (
                                                      <Checkbox
                                                        isChecked={isSelected}
                                                        id={`${ID_PREFIX_CHECKBOX_EXPORT_CARD}-${contact.id}`}
                                                      />
                                                    ) : (
                                                      <Fragment>
                                                        {
                                                          (!!contact.photoThumbnail || !!contact.photo) && (
                                                            <PaddedFlexContainer>
                                                              <Image
                                                                text={contact.fullName}
                                                                link={contact.photoThumbnail || contact.photo}
                                                              />
                                                            </PaddedFlexContainer>
                                                          )
                                                        }
                                                        {
                                                          (!contact.photoThumbnail && !contact.photo) && (
                                                            <LetterAvatar
                                                              wordList={(
                                                                contact.type && contact.type === CONTACT_TYPE_COMPANY
                                                                  ? [contact.name]
                                                                  : [contact.firstName, contact.lastName]
                                                              )}
                                                            />
                                                          )
                                                        }
                                                      </Fragment>
                                                    )
                                                  }
                                                  <PaddedFlexContainer>
                                                    <FilteredLink
                                                      link={!isExportMode && !isSelectMode && !isEditMode
                                                        && !isCustomizeMode && `${ROUTE_CONTACT}/${contact.id}`}
                                                      permission={CONTACT_VIEW}
                                                    >
                                                      {contact.fullName}
                                                    </FilteredLink>
                                                  </PaddedFlexContainer>
                                                </FlexContainer>
                                                {
                                                  !isMobileScreen && (
                                                    <Fragment>
                                                      {
                                                        isEditMode && !isActive && (
                                                          <CheckAccess permissions={contactEditPermissions}>
                                                            <ActionButton
                                                              withNoPadding
                                                              withFixedSize
                                                              isHidden={!!isFormSaving}
                                                              onClick={(
                                                                event: React.MouseEvent<Element, MouseEvent>,
                                                              ): void => {
                                                                onInlineEditModeButtonClick(event, contact.id);
                                                              }}
                                                            >
                                                              <EditIcon />
                                                            </ActionButton>
                                                          </CheckAccess>
                                                        )
                                                      }
                                                      {
                                                        !isEditMode && (
                                                          <CheckAccess
                                                            permissions={(
                                                              actionMenuItems && actionMenuItems.map((
                                                                actionMenuItem: ActionMenuItem,
                                                              ): string | undefined => (
                                                                actionMenuItem.permission
                                                              ))
                                                            )}
                                                          >
                                                            {
                                                              !isExportMode && !isSelectMode && !contact.user && (
                                                                <ActionButton
                                                                  withPopup
                                                                  withNoPadding
                                                                  withFixedSize
                                                                  onClick={openActionMenu}
                                                                  {...{
                                                                    [DATA_CONTACT_ID]: contact.id,
                                                                  }}
                                                                >
                                                                  <MoreIcon />
                                                                </ActionButton>
                                                              )
                                                            }
                                                          </CheckAccess>
                                                        )
                                                      }
                                                    </Fragment>
                                                  )
                                                }
                                              </FlexContainer>
                                            </GridItem>
                                            {
                                              !!contact.company && (
                                                <GridItemCardViewLine headerTitle="Company">
                                                  <ChipLink
                                                    label={contact.company.name}
                                                    isDisabled={isEditMode || isCustomizeMode
                                                      || isExportMode || isSelectMode}
                                                    imageLink={contact.company.photoThumbnail || contact.company.photo
                                                      || CompanyDefaultImage}
                                                    permission={CONTACT_VIEW}
                                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                      if (contact.company) {
                                                        onContactClick(event, contact.company.id);
                                                      }
                                                    }}
                                                  />
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              !!contact.dealsTotal && (
                                                <GridItemCardViewLine headerTitle="Deals in progress">
                                                  <TextBold>
                                                    {contact.dealsTotal}
                                                  </TextBold>
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              !!contact.phone && (
                                                <GridItemCardViewLine icon={<PhoneIcon />}>
                                                  {
                                                    contact.phone && contact.phoneText && (
                                                      <FilteredLink
                                                        isPhone
                                                        link={!isExportMode && !isSelectMode
                                                          && !isEditMode && !isCustomizeMode
                                                          && FormatService.formatCleanIntegerNumberString(
                                                            contact.phone,
                                                          )}
                                                        phoneExtension={contact.phoneExtension}
                                                      >
                                                        {
                                                          `${contact.phoneText}${contact.phoneExtension
                                                            ? ` ext. ${contact.phoneExtension}` : ''}`
                                                        }
                                                      </FilteredLink>
                                                    )
                                                  }
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              !!contact.phoneWork && (
                                                <GridItemCardViewLine icon={<PhoneExtraIcon />}>
                                                  {
                                                    !!contact.phoneWork && !!contact.phoneWorkText && (
                                                      <FilteredLink
                                                        isPhone
                                                        link={!isExportMode && !isSelectMode
                                                          && !isEditMode && !isCustomizeMode
                                                          && FormatService.formatCleanIntegerNumberString(
                                                            contact.phoneWork,
                                                          )}
                                                        phoneExtension={contact.phoneWorkExtension}
                                                      >
                                                        {
                                                          `${contact.phoneWorkText}${contact.phoneWorkExtension
                                                            ? ` ext. ${contact.phoneWorkExtension}` : ''}`
                                                        }
                                                      </FilteredLink>
                                                    )
                                                  }
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              !!contact.email && (
                                                <GridItemCardViewLine icon={<EmailIcon />}>
                                                  <FilteredLink
                                                    isEmail
                                                    link={!isExportMode && !isSelectMode
                                                      && !isEditMode && !isCustomizeMode && contact.email}
                                                  >
                                                    {contact.email}
                                                  </FilteredLink>
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              (!!contact.location || !!contact.streetAddress) && (
                                                <GridItemCardViewLine icon={<MapIcon />}>
                                                  {
                                                    (!!contact.streetAddress && !!contact.address) ? (
                                                      <FilteredLink
                                                        isExternal
                                                        link={!isExportMode && !isSelectMode && !isEditMode
                                                          && !isCustomizeMode && contact.address && contact.address.url}
                                                      >
                                                        {
                                                          FormatService.formatStringAddress(
                                                            contact.streetAddress,
                                                            contact.location,
                                                            contact.postalCode,
                                                          )
                                                        }
                                                      </FilteredLink>
                                                    ) : (
                                                      FormatService.formatStringAddress(
                                                        contact.streetAddress,
                                                        contact.location,
                                                        contact.postalCode,
                                                      )
                                                    )
                                                  }
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              !!contact.addressDetails && (
                                                <GridItemCardViewLine headerTitle="Address Details">
                                                  {
                                                    FormatService.formatLimitedString(
                                                      contact.addressDetails, FormatService.TEXT_LIMIT_TABLE,
                                                    )
                                                  }
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              !!contact.linkedin && (
                                                <GridItemCardViewLine icon={<LinkedinIcon />}>
                                                  <FilteredLink
                                                    isExternal
                                                    link={!isExportMode && !isSelectMode
                                                      && !isEditMode && !isCustomizeMode && contact.linkedin}
                                                  >
                                                    {contact.linkedin}
                                                  </FilteredLink>
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              !!contact.title && (
                                                <GridItemCardViewLine headerTitle="Title">
                                                  {contact.title}
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              contact.groups && !!contact.groups.length && (
                                                <GridItemCardViewLine headerTitle="Groups">
                                                  {
                                                    contact.groups.map((
                                                      contactGroupItem: ContactGroup,
                                                    ): ReactElement => (
                                                      <Chip
                                                        isDisabled={isEditMode || isExportMode || isSelectMode
                                                          || isCustomizeMode}
                                                        key={contactGroupItem.id}
                                                        label={contactGroupItem.title}
                                                      />
                                                    ))
                                                  }
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              contact.assetTypes && !!contact.assetTypes.length && (
                                                <GridItemCardViewLine headerTitle="Asset Focus">
                                                  {
                                                    contact.assetTypes.map((assetTypeItem: AssetType): ReactElement => (
                                                      <Chip
                                                        isDisabled={isEditMode || isExportMode || isSelectMode
                                                          || isCustomizeMode}
                                                        key={assetTypeItem.id}
                                                        label={assetTypeItem.title}
                                                      />
                                                    ))
                                                  }
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              contact.tags && !!contact.tags.length && (
                                                <GridItemCardViewLine headerTitle="Tags">
                                                  {
                                                    contact.tags.map((tagItem: Tag): ReactElement => (
                                                      <ChipLink
                                                        isDisabled={isEditMode || isCustomizeMode
                                                          || isExportMode || isSelectMode}
                                                        key={tagItem.id}
                                                        label={tagItem.name}
                                                        permission={SEARCH_RESULT_VIEW}
                                                        onClick={(
                                                          event: React.MouseEvent<Element, MouseEvent>,
                                                        ): void => {
                                                          onTagClick(event, tagItem.id);
                                                        }}
                                                      />
                                                    ))
                                                  }
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              (
                                                (contact.type === CONTACT_TYPE_PERSON && contact.company)
                                                || (contact.type === CONTACT_TYPE_COMPANY && contact.relatedContacts
                                                  && !!contact.relatedContacts.length)
                                              ) && (
                                                <GridItemCardViewLine headerTitle="Related Contacts">
                                                  {
                                                    contact.type === CONTACT_TYPE_COMPANY && contact.relatedContacts
                                                    && !!contact.relatedContacts.length && contact.relatedContacts.map((
                                                      contactItem: Contact,
                                                    ): ReactElement => (
                                                      <ChipLink
                                                        isDisabled={isEditMode || isCustomizeMode
                                                          || isExportMode || isSelectMode}
                                                        key={contactItem.id}
                                                        label={contactItem.fullName}
                                                        imageLink={contactItem.photoThumbnail || contactItem.photo
                                                          || PersonDefaultImage}
                                                        permission={CONTACT_VIEW}
                                                        onClick={(
                                                          event: React.MouseEvent<Element, MouseEvent>,
                                                        ): void => {
                                                          onContactClick(event, contactItem.id);
                                                        }}
                                                      />
                                                    ))
                                                  }
                                                  {
                                                    contact.type === CONTACT_TYPE_PERSON && !!contact.company && (
                                                      <ChipLink
                                                        isDisabled={isEditMode || isCustomizeMode
                                                          || isExportMode || isSelectMode}
                                                        key={contact.company.id}
                                                        label={contact.company.name}
                                                        imageLink={contact.company.photoThumbnail
                                                          || contact.company.photo || CompanyDefaultImage}
                                                        permission={CONTACT_VIEW}
                                                        onClick={(
                                                          event: React.MouseEvent<Element, MouseEvent>,
                                                        ): void => {
                                                          if (contact.company) {
                                                            onContactClick(event, contact.company.id);
                                                          }
                                                        }}
                                                      />
                                                    )
                                                  }
                                                </GridItemCardViewLine>
                                              )
                                            }
                                            {
                                              isUserPlatformAdmin && (
                                                <GridItemCardViewLine headerTitle="Is Default">
                                                  {FormatService.formatBooleanValue(contact.isDefault)}
                                                </GridItemCardViewLine>
                                              )
                                            }
                                          </Grid>
                                        </LongClickCard>
                                      )
                                    }
                                  </GridItemCardViewBlock>
                                );
                              })
                            }
                          </Grid>
                        ) : (
                          <Card>
                            {
                              !isSmallScreen ? (
                                <ContentListContainerOffset
                                  withTabs
                                  withFilters={withFilters}
                                  withButtons={withButtons}
                                  withMap={withMap}
                                  withPagination={withPagination}
                                >
                                  <TableResize
                                    isActive={isCustomizeMode}
                                    isDisabled={isEditMode}
                                    tableRef={tableRef}
                                    headerCellRefs={headerCellRefs}
                                    tableColumnSettings={tableColumnSettings}
                                    onTableColumnsChange={onTableColumnSettingsChange}
                                  >
                                    <Table withStickyHeader isFullWidth={isEditMode} ref={tableRef}>
                                      <TableHead isHeader>
                                        <TableRow>
                                          {
                                            (isExportMode || isSelectMode) && (
                                              <TableCell isSticky isStickyLeft />
                                            )
                                          }
                                          {
                                            isUserPlatformAdmin && !isCustomizeMode && (
                                              <TableCellShort>
                                                Is Default
                                              </TableCellShort>
                                            )
                                          }
                                          {
                                            extendedTableFields.map((
                                              tableField: TableField, tableFieldIndex: number,
                                            ): ReactElement => (
                                              <VisibleCell
                                                isEditMode={isEditMode}
                                                key={tableField.id}
                                                entityType={filterContactType}
                                                tableField={tableField}
                                              >
                                                <FilteredCell
                                                  columnName={tableField.name}
                                                  tableName={TABLE_NAME_CONTACT_LIST}
                                                  currentUser={currentUser}
                                                >
                                                  <TableCell
                                                    isActions={tableField.name === HEADER_NAME_ACTIONS}
                                                    isActionsHeader={tableField.name === HEADER_NAME_ACTIONS}
                                                    isTextRight={tableField.name === HEADER_NAME_ACTIONS}
                                                    isSticky={!isCustomizeMode && tableField.isSticky}
                                                    isStickyLeft={!isCustomizeMode && !isExportMode && !isSelectMode
                                                      && tableField.isSticky && !tableField.isStickyRight}
                                                    isStickyLeftSelect={!isCustomizeMode
                                                      && (isExportMode || isSelectMode) && tableField.isSticky
                                                      && !tableField.isStickyRight}
                                                    isStickyLeftHeader={!isCustomizeMode && !isExportMode
                                                      && !isSelectMode && tableField.isSticky
                                                      && !tableField.isStickyRight}
                                                    isStickyRight={!isCustomizeMode && tableField.isSticky
                                                      && tableField.isStickyRight}
                                                    isStickyRightHeader={!isCustomizeMode
                                                      && !isExportMode && !isSelectMode
                                                      && tableField.isSticky && tableField.isStickyRight}
                                                    ref={(ref: HTMLElement): void => {
                                                      if (headerCellRefs.current) {
                                                        headerCellRefs.current[tableFieldIndex] = ref;
                                                      }
                                                    }}
                                                    sortDirection={sortParams.field === tableField.name
                                                    && tableField.isSortable ? sortParams.type : false}
                                                  >
                                                    {
                                                      tableField.isSortable && !isEditMode ? (
                                                        <DraggableItem
                                                          isDisabled={!isCustomizeMode}
                                                          itemId={tableField.id}
                                                          index={tableFieldIndex}
                                                          dropDeps={tableColumnDropDeps}
                                                          onDropItem={onDropColumn}
                                                        >
                                                          <TableSortLabel
                                                            isActive={sortParams.field === tableField.name}
                                                            withNoLabel={tableField.isHeaderNameHidden}
                                                            sortType={sortParams.type}
                                                            onClick={(): void => {
                                                              sortTableRows(tableField.name);
                                                            }}
                                                          >
                                                            {tableField.title}
                                                          </TableSortLabel>
                                                        </DraggableItem>
                                                      ) : (
                                                        !tableField.isHeaderNameHidden && (
                                                          <DraggableItem
                                                            isDisabled={!isCustomizeMode}
                                                            itemId={tableField.id}
                                                            index={tableFieldIndex}
                                                            dropDeps={tableColumnDropDeps}
                                                            onDropItem={onDropColumn}
                                                          >
                                                            {tableField.title}
                                                            {tableField.isRequired && isEditMode && <RequiredMark />}
                                                          </DraggableItem>
                                                        )
                                                      )
                                                    }
                                                  </TableCell>
                                                </FilteredCell>
                                              </VisibleCell>
                                            ))
                                          }
                                        </TableRow>
                                      </TableHead>
                                      <TableBody
                                        isPlain
                                        withZebraRows
                                        withBorderedCells={!isCustomizeMode}
                                        withFixedCellsSizeExtended={isEditMode}
                                      >
                                        {
                                          contactListData.map((
                                            contact: Contact, contactIndex: number,
                                          ): ReactElement => {
                                            const isSelected: boolean = selectedRows
                                              ? withAllRowsSelected || checkIsRowSelected(contact.id) : false;
                                            const isActive: boolean = checkIsContactActive(contact.id);
                                            const isInlineSaving: boolean = inlineSaveContactId !== null
                                              ? contact.id === inlineSaveContactId : isFormSaving;

                                            return (
                                              <ContactRow
                                                isActive={isActive}
                                                isSaving={isFormSaving}
                                                isSaveDisabled={isSaveDisabled}
                                                isInlineSaving={isInlineSaving}
                                                isSelected={isSelected}
                                                isEditMode={isEditMode}
                                                isExportMode={isExportMode}
                                                isSelectMode={isSelectMode}
                                                isCustomizeMode={isCustomizeMode}
                                                key={contact.id}
                                                contactIndex={contactIndex}
                                                contactItem={contact}
                                                inputRefs={inputRefs}
                                                filterContactType={filterContactType}
                                                fileDropDeps={fileDropDeps}
                                                tableName={TABLE_NAME_CONTACT_LIST}
                                                tableColumnSettings={tableColumnSettings}
                                                tableContactListFields={extendedTableFields}
                                                currentUser={currentUser}
                                                contactTypeList={contactTypeList}
                                                contactCompanyList={contactCompanyList}
                                                assetTypeList={assetTypeList}
                                                contactGroupList={contactGroupList}
                                                tagList={tagList}
                                                onRowClick={onRowClick}
                                                onLongClick={onLongClick}
                                                onInlineSave={onInlineSave}
                                                onInlineCancel={onInlineCancel}
                                                onContactPhotoChange={onContactPhotoChange}
                                                onContactPhotoDelete={onContactPhotoDelete}
                                                onPhoneChange={onContactPhoneChange}
                                                onPhoneWorkChange={onContactPhoneWorkChange}
                                                onAddressFieldKeyDown={onAddressFieldKeyDown}
                                                onAddressSearchCompleted={onAddressSearchCompleted}
                                                onAssetTypesChange={onAssetTypesChange}
                                                onContactGroupsChange={onContactGroupsChange}
                                                onTagsChange={onContactTagsChange}
                                                onContactCompanySearch={onContactCompanySearch}
                                                onContactCompanySearchClose={onContactCompanySearch}
                                                onTagSearch={onTagSearch}
                                                onTagSearchClose={onTagSearchClose}
                                              />
                                            );
                                          })
                                        }
                                      </TableBody>
                                    </Table>
                                  </TableResize>
                                </ContentListContainerOffset>
                              ) : (
                                <ContentListContainerOffset
                                  isDefault
                                  withTabs
                                  withFilters={withFilters}
                                  withButtons={withButtons}
                                  withMap={withMap}
                                  withPagination={withPagination}
                                >
                                  <List>
                                    {
                                      contactListData && !!contactListData.length
                                      && contactListData.map((contact: Contact): ReactElement => {
                                        const isSelected: boolean = selectedRows
                                          ? withAllRowsSelected || checkIsRowSelected(contact.id) : false;
                                        const contactGroupsString: string = (contact.groups && contact.groups.length
                                          ? contact.groups.map((contactGroupItem: ContactGroup): string => (
                                            contactGroupItem.title
                                          )) : []).join(', ');

                                        return (
                                          <LongClickListItem
                                            isButton={!isSelectMode && !isExportMode}
                                            isActive={isSelected}
                                            key={contact.id}
                                            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                              onRowClick(event, contact.id, true);
                                            }}
                                            onLongClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                              onLongClick(event, contact.id);
                                            }}
                                          >
                                            {
                                              (isExportMode || isSelectMode) && (
                                                <ListItemCheckbox isChecked={isSelected} itemId={contact.id} />
                                              )
                                            }
                                            {
                                              (!!contact.photoThumbnail || !!contact.photo) && (
                                                <PaddedFlexContainer>
                                                  <Image
                                                    isListImage
                                                    text={contact.fullName}
                                                    link={contact.photoThumbnail || contact.photo}
                                                  />
                                                </PaddedFlexContainer>
                                              )
                                            }
                                            {
                                              (!contact.photoThumbnail && !contact.photo) && (
                                                <LetterAvatar
                                                  wordList={contact.type && contact.type === CONTACT_TYPE_COMPANY
                                                    ? [contact.name] : [contact.firstName, contact.lastName]}
                                                />
                                              )
                                            }
                                            <ListItemText isBlock>
                                              {contact.fullName}
                                            </ListItemText>
                                            <ListItemText
                                              isBlock
                                              isBlockRight
                                              subText={!isEditMode ? contactGroupsString : (
                                                <FlexContainer isEnd>
                                                  <ActionButton
                                                    withSmallPadding
                                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                      onRowClick(event, contact.id, true);
                                                    }}
                                                  >
                                                    <ArrowLinkIcon />
                                                  </ActionButton>
                                                </FlexContainer>
                                              )}
                                            />
                                          </LongClickListItem>
                                        );
                                      })
                                    }
                                  </List>
                                </ContentListContainerOffset>
                              )
                            }
                          </Card>
                        )
                      }
                      {
                        !isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && (
                          <TablePagination
                            page={page}
                            limit={limit}
                            totalItems={contactTotal}
                            onPageChange={onPageChange}
                            onLimitChange={onLimitChange}
                          />
                        )
                      }
                    </Fragment>
                  ) : (
                    <NoContentBlock>
                      No contacts found by your request
                    </NoContentBlock>
                  )
                }
              </Fragment>
            )}
            onSetTabIndex={setTabIndex}
            onTabChange={onTabChange}
            onBeforeTabChange={onBeforeTabChange}
          />
          <CheckAccess permissions={contactEditPermissions}>
            <ControlButtonBlock
              isExportMode={isExportMode}
              isSelectMode={isSelectMode}
              isEditMode={isEditMode}
              isSaveDisabled={isSaveDisabled}
              isExportDisabled={isExportDisabled}
              withOffset={isRightDrawerOpen}
              onCancelForm={onCancelForm}
              onExportClick={openExportModal}
              onSubmitForm={onSubmitForm}
            />
            {
              (!!formError || !!mainServerFormError) && !isFormSaving && (
                <FormError
                  withShowError={isEditMode}
                  withLeftOffset={isRightDrawerOpen}
                  error={formError || mainServerFormError}
                />
              )
            }
            {
              isFormSaving && isEditMode && (
                <Loader isInline isSubmit isFloating isHidden={isMobileScreen} />
              )
            }
          </CheckAccess>
        </PullToRefresh>
        <ContactModal
          isOpen={isContactModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          currentUser={currentUser}
          contactPhotoFile={contactPhotoFiles[FormService.FORM_KEY_NEW]}
          contactPhotoPreviewUrl={contactPhotoPreviewUrl}
          contactAddressNewItems={contactAddressNewItems[FormService.FORM_KEY_NEW]}
          contactAddressDetailsItems={contactAddressDetailsItems[FormService.FORM_KEY_NEW]}
          contactGroupList={contactGroupList}
          contactTypeList={contactTypeList}
          contactCompanyList={contactCompanyList}
          assetTypeList={assetTypeList}
          tagList={tagList}
          saveFormDeps={saveFormDeps}
          onCreate={onContactCreate}
          onCancel={onContactModalCancel}
          onCloseModal={closeContactModal}
          onContactPhotoChange={onContactPhotoChange}
          onContactPhotoDelete={onContactPhotoDelete}
          onContactGroupsChange={onContactGroupsChange}
          onWebsiteChange={onContactWebsiteChange}
          onEmailChange={onContactEmailChange}
          onTagsChange={onContactTagsChange}
          onPhoneChange={onContactPhoneChange}
          onPhoneWorkChange={onContactPhoneWorkChange}
          onAddressSearchCompleted={onAddressSearchCompleted}
          onAddressFieldKeyDown={onAddressFieldKeyDown}
          onAssetTypesChange={onAssetTypesChange}
          onSubmitCreateForm={onContactSubmitCreateForm}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onTagSearch={onTagSearch}
          onTagSearchClose={onTagSearchClose}
          onContactAddressDetailsClose={onContactAddressDetailsClose}
          onContactAddressDetailsOpen={onContactAddressDetailsOpen}
          onContactAddressDeleteItemClick={onContactAddressDeleteItemClick}
          onContactAddressAddNewItem={onContactAddressAddNewItem}
        />
        <ContextMenu
          menuAnchor={menuAnchor}
          actionMenuItems={actionMenuItems}
          dataAttributeEntityId={DATA_CONTACT_ID}
          onClose={closeActionMenu}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeDeleteConfirmModal}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onCloseModal={closeLeaveConfirmModal}
          onConfirm={onLeaveConfirm}
        />
        <ExportModal
          isOpen={isExportModalOpen}
          entityIds={contactActiveIds}
          exportEntityType={IMPORT_EXPORT_ENTITY_TYPE_CONTACT_LIST}
          withExportAll={withAllRowsSelected}
          exportFormatList={FORMAT_LIST_EXPORT_CONTACT}
          onSuccess={onExportSuccess}
          onCloseModal={closeExportModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    contactList,
    controls,
    rightDrawer,
    app,
  } = reducers;

  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  const contactListData: Contact[] = contactList.contactListData && contactList.contactListData.contactList;

  if (contactListData) {
    initialValues.contactListData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        name: null,
        firstName: null,
        lastName: null,
        groupIds: null,
        type: CONTACT_TYPE_COMPANY,
        addresses: {},
      },
    };
    contactListData.forEach((contactItem: Contact): void => {
      const formKeyContact: string = FormService.getIndexedFormFieldKey(contactItem.id);

      initialValues.contactListData[formKeyContact] = {
        id: contactItem.id,
        userId: contactItem.user ? contactItem.user.id : null,
        name: contactItem.name,
        firstName: contactItem.firstName,
        lastName: contactItem.lastName,
        companyId: contactItem.company ? contactItem.company.id : null,
        type: contactItem.type,
        groupIds: contactItem.groups,
        phone: contactItem.phone,
        phoneExtension: contactItem.phoneExtension,
        phoneWork: contactItem.phoneWork,
        phoneWorkExtension: contactItem.phoneWorkExtension,
        email: contactItem.email,
        streetAddress: contactItem.streetAddress,
        location: contactItem.location,
        postalCode: contactItem.postalCode,
        addressDetails: contactItem.addressDetails,
        linkedin: contactItem.linkedin,
        title: contactItem.title,
        description: contactItem.description,
        industry: contactItem.industry,
        foundationYear: contactItem.foundationYear,
        headQuarter: contactItem.headQuarter,
        website: contactItem.website,
        keySubsidiaries: contactItem.keySubsidiaries,
        tags: contactItem.tags,
        assetTypes: contactItem.assetTypes,
        address: contactItem.address,
        isDefault: contactItem.isDefault,
        photo: null,
        photoLink: null,
      };
    });
  }

  return {
    isDataLoaded: !!contactList.contactListData,
    isEditMode: controls.isEditMode,
    isCustomizeMode: controls.isCustomizeMode,
    isExportMode: controls.isExportMode,
    isSelectMode: controls.isSelectMode,
    isCardView: controls.isCardView,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    contactCompanyData: contactList.contactCompanyData,
    contactPersonData: contactList.contactPersonData,
    contactListData: contactList.contactListData && contactList.contactListData.contactList,
    contactTotal: contactList.contactListData && contactList.contactListData.contactTotal,
    contactCompanyList: contactList.contactListData && contactList.contactListData.contactCompanyList,
    contactGroupList: contactList.contactListData && contactList.contactListData.contactGroupList,
    contactTypeList: contactList.contactListData && contactList.contactListData.contactTypeList,
    filterContactTypeList: contactList.contactListData && contactList.contactListData.contactListFilters
      && contactList.contactListData.contactListFilters.contactTypeList,
    filterContactCompanyList: contactList.contactListData && contactList.contactListData.contactListFilters
      && contactList.contactListData.contactListFilters.contactCompanyList,
    filterLocationList: contactList.contactListData && contactList.contactListData.contactListFilters
      && contactList.contactListData.contactListFilters.locationList,
    filterAssetTypeList: contactList.contactListData && contactList.contactListData.contactListFilters
      && contactList.contactListData.contactListFilters.assetTypeList,
    filterTagList: contactList.contactListData && contactList.contactListData.contactListFilters
      && contactList.contactListData.contactListFilters.tagList,
    tagList: contactList.contactListData && contactList.contactListData.tagList,
    assetTypeList: contactList.contactListData && contactList.contactListData.assetTypeList,
  };
};

const ContactListForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ contactList }: Reducers): ValidationErrorsMap => (
    contactList.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ contactList }: Reducers): ValidationErrorsMap => (
    contactList.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_CONTACT_LIST}
      component={ContactList}
      validator={ContactListValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(ContactListForm);
